(function () {
  const root = document.querySelector('.arb[data-section-id]:not([data-arb-initialized])');
  if (!root) return;
  root.setAttribute('data-arb-initialized', 'true');
  const bootEl = root.querySelector('[data-arb-boot]');
  const boot = JSON.parse(bootEl.textContent || '{}');
  const ui = root.querySelector('[data-arb-root]');

  const STEPS = ['cleanser', 'toner', 'serum', 'moisturizer', 'sunscreen'];
  const REQUIRED_STEPS = ['cleanser', 'moisturizer', 'sunscreen'];
  const STEP_ORDER_PICK = ['serum', 'toner', 'cleanser', 'moisturizer', 'sunscreen'];
  const CONCERN_STEP_MULT = {
    cleanser: 1.0,
    toner: 1.15,
    serum: 1.35,
    moisturizer: 1.05,
    sunscreen: 0.85
  };
  const RELATED_CONCERNS = {
    pores: ['acne'],
    acne: ['pores'],
    pigmentation: [],
    dryness: [],
    wrinkles: []
  };
  const SKIN_TEXTURE_PREF = {
    oily: { gel: 18, watery: 14, cream: -22 },
    combination: { gel: 14, watery: 12, cream: -10 },
    dry: { cream: 18, watery: 4, gel: -8 },
    sensitive: { watery: 10, cream: 8, gel: 4 },
    normal: { watery: 8, gel: 6, cream: 6 }
  };
  const QUALITY_FLOOR = 55;

  /**
   * How well a product labelled for skin type X suits a customer with skin
   * type Y. Replaces the old boolean gate, which excluded any product whose
   * label didn't match — so labelling a product could only ever narrow where
   * it appeared. Here a mismatch is a cost the score must overcome, and an
   * unlabelled product sits deliberately below a confirmed match.
   */
  const SKIN_AFFINITY = {
    oily: { oily: 1, combination: 0.5, sensitive: 0.25, normal: 0.2, dry: -0.75 },
    combination: { combination: 1, oily: 0.45, normal: 0.3, sensitive: 0.28, dry: 0.22 },
    dry: { dry: 1, sensitive: 0.45, normal: 0.22, combination: 0.2, oily: -0.75 },
    sensitive: { sensitive: 1, dry: 0.45, normal: 0.15, combination: 0.1, oily: -0.6 },
    normal: { normal: 1, combination: 0.45, sensitive: 0.32, dry: 0.2, oily: 0.2 }
  };
  /** Applied when a product carries no skin label at all: possible, but unproven. */
  const SKIN_AFFINITY_UNKNOWN = 0.2;
  const SKIN_AFFINITY_WEIGHT = { cleanser: 36, toner: 40, serum: 40, moisturizer: 42, sunscreen: 34 };
  /**
   * Mismatches cost more than matches pay. Skin type is a comfort-and-safety
   * constraint, so a strong concern match should not casually override it —
   * but it can when the shelf offers nothing better.
   */
  const SKIN_MISMATCH_MULTIPLIER = 1.6;
  /**
   * Paid on top of affinity when a product is labelled for exactly this skin
   * type. Adjacent labels are acceptable, but "made for your skin" is a
   * stronger claim than "close enough", and without this the concern terms on
   * the treatment steps outweigh skin fit roughly three to one.
   */
  const SKIN_EXACT_BONUS = 16;

  /**
   * Weight of concern relevance per step. The treatment steps carry the
   * concern; base steps must be appropriate but not redundant treatments.
   */
  const CONCERN_FIT_WEIGHT = { cleanser: 12, toner: 74, serum: 96, moisturizer: 60, sunscreen: 55 };
  /** Cleanser only earns treatment-level concern weight for rinse-off-effective actives (BHA/clay/tea-tree = pores/acne). */
  const CLEANSER_CONCERN_WEIGHT_BOOSTED = 55;
  function getConcernFitWeight(step, concern) {
    if (step === 'cleanser') {
      return concern === 'pores' || concern === 'acne' ? CLEANSER_CONCERN_WEIGHT_BOOSTED : CONCERN_FIT_WEIGHT.cleanser;
    }
    return CONCERN_FIT_WEIGHT[step] || 10;
  }
  /**
   * Which support steps should be built with a hard skin∩concern priority
   * queue (2026-08-07 audit fix). Serum/toner always did. Moisturizer and
   * sunscreen now do too (leave-on enough to carry real treatment value).
   * Cleanser only for pores/acne — rinse-off contact time only meaningfully
   * treats oil-control/exfoliating actives (salicylic/clay/tea-tree/benzoyl),
   * which ACTIVE_LEXICON already scopes to pores/acne only.
   */
  function stepWantsConcernPriority(step, concern) {
    if (step === 'serum' || step === 'toner' || step === 'moisturizer' || step === 'sunscreen') return true;
    if (step === 'cleanser') return concern === 'pores' || concern === 'acne';
    return false;
  }
  /** Below this much evidence a treatment step is not really treating anything. */
  const TREATMENT_FIT_FLOOR = 0.22;

  /**
   * ---------------------------------------------------------------- LEXICONS
   * Single source of truth for every keyword the engine reasons about.
   * Previously the same vocabulary was duplicated across inference, filtering
   * and scoring with different word lists, so a term could earn a scoring
   * bonus it could never reach (e.g. PDRN). Declaring it once keeps the
   * layers in sync and makes every decision auditable.
   *
   * Two conventions every pattern here must follow:
   *  1. Write Arabic in NORMALIZED form only — normalize() folds أإآ→ا, ة→ه,
   *     ى→ي, so a pattern containing "شجرة" or "قطنية" can never match.
   *     audit-lexicon.mjs enforces this.
   *  2. Arabic titles use the definite article freely ("واقي الشمس" vs
   *     "واقي شمس"), so multi-word phrases must allow an optional "ال".
   */

  /** Step detection. Order matters: first match wins in inferStep. */
  const STEP_PATTERNS = {
    sunscreen: /واقي\s*(?:ال)?شمس|واقي\s*من\s*(?:ال)?شمس|sun\s*(?:screen|cream|serum|fluid|milk|essence|gel)|sunscreen|relief sun|\bspf\s*\d+/,
    toner: /تونر|toner|منظفات\s*قطن|قطنيه|clear pad|pore pad|\bpads?\b|ميست|\bmist\b|ماء\s*مي?سيلار|micellar/,
    cleanser: /غسول|cleanser|cleansing foam|foam cleanser|washing gel|face wash|cleansing gel|gel cleanser|صابون|\bsoap\b/,
    serum: /سير[وو]?م|serum|ampoule|امبول|essence|اسنس|ايسنس|اسينس|reedle|ريدل/,
    moisturizer: /مرطب|moisturizer|moisturiser|jelly cream|gel cream|\bcream\b|كريم|لوشن|\blotion\b|emulsion|gel lotion|soothing gel|(?:^|\s)جل(?=\s|$)/
  };

  /** Concern vocabulary, shared by concern inference and scoring nudges. */
  const CONCERN_PATTERNS = {
    pores: /مسام|pore|sebum|زهم|blackhead|رؤوس\s*سوداء|oil\s*control|تحكم.*(?:زيوت|دهون)|طين\s*(?:ال)?بركان|بركان|volcanic|volcan|jeju volcanic|clay cleanser|غسول\s*(?:ال)?طين|تنقيه\s*(?:ال)?مسام|تصغير\s*(?:ال)?مسام|شد\s*(?:ال)?مسام/,
    acne: /حبوب|حب\s*(?:ال)?شباب|acne|بثور|blemish|عيوب|salicylic|ساليسيليك|\bbha\b|azelaic|ازيليك|tea.?tree|شجره\s*(?:ال)?شاي/,
    pigmentation: /تصبغ|بقع|dark.?spot|pigment|تفتيح|توحيد|bright|glow|توهج|نضاره|اشراق|whiten|خوخ|peach|(?:ال)?ارز|rice|arbutin|اربوتين|\btxa\b|tranexamic|ترانيكساميك|kojic|كوجيك|glutathione|جلوتاثيون|vitamin\s*c|فيتامين\s*(?:سي|c)/,
    dryness: /جفاف|تقشر|dehydr|dryness|hydration|ترطيب|هيالورون|hyaluron|barrier|حاجز|ceramide|سيراميد|snail|حلزون|beta.?glucan|بيتا.?جلوكان|squalane|سكوالين|panthenol|بانثينول|اطو|ato /,
    wrinkles: /تجاعيد|خطوط|wrinkle|retinol|retin|peptide|ببتيد|كولاجين|collagen|anti.?aging|مرونه|bakuchiol|باكوتشيول|\bpdrn\b|شد\s*(?:ال)?بشره|lifting/
  };

  /**
   * Potent treatment actives. `intensity` feeds the irritation budget and
   * `concerns` is the only place an active-to-concern relationship is declared.
   * Supporting ingredients (hydrators, soothers) belong in INGREDIENT_LEXICON,
   * not here — listing them as actives would wrongly consume the budget.
   */
  const ACTIVE_LEXICON = [
    { family: 'retinoid', intensity: 2, concerns: ['wrinkles', 'acne', 'pigmentation'], re: /retinol|retinal|retinoid|ريتينول|ريتينال/ },
    { family: 'exfoliating-acid', intensity: 2, concerns: ['pigmentation', 'pores', 'acne'], re: /\baha\b|\bpha\b|glycolic|lactic acid|mandelic|جليكوليك|لاكتيك|ماندليك|peeling|تقشير/ },
    { family: 'salicylic', intensity: 2, concerns: ['acne', 'pores'], re: /salicylic|ساليسيليك|\bbha\b/ },
    { family: 'benzoyl', intensity: 2, concerns: ['acne'], re: /benzoyl|بنزويل/ },
    { family: 'clay', intensity: 1, concerns: ['pores'], re: /volcan|بركان|بركاني|فولكانيك|clay|طين|charcoal|فحم/ },
    { family: 'azelaic', intensity: 1, concerns: ['acne', 'pigmentation'], re: /azelaic|ازيليك/ },
    { family: 'vitamin-c', intensity: 1, concerns: ['pigmentation', 'wrinkles'], re: /vitamin.?c\b|فيتامين\s*(?:سي|c)|ascorbic|اسكوربيك/ },
    { family: 'niacinamide', intensity: 1, concerns: ['pores', 'pigmentation', 'acne'], re: /niacinamide|نياسيناميد/ },
    { family: 'bakuchiol', intensity: 1, concerns: ['wrinkles'], re: /bakuchiol|باكوتشيول/ },
    { family: 'succinic', intensity: 1, concerns: ['acne', 'pores'], re: /succinic|سكسينيك/ },
    { family: 'kojic', intensity: 1, concerns: ['pigmentation'], re: /kojic|كوجيك/ },
    { family: 'tea-tree', intensity: 1, concerns: ['acne', 'pores'], re: /tea.?tree|شجره\s*(?:ال)?شاي|تي\s*تري/ },
    { family: 'tranexamic', intensity: 0, concerns: ['pigmentation'], re: /tranexamic|ترانيكساميك|\btxa\b/ },
    { family: 'arbutin', intensity: 0, concerns: ['pigmentation'], re: /arbutin|اربوتين/ },
    { family: 'glutathione', intensity: 0, concerns: ['pigmentation'], re: /glutathione|جلوتاثيون/ },
    { family: 'peptide', intensity: 0, concerns: ['wrinkles'], re: /peptide|ببتيد|matrixyl/ },
    { family: 'pdrn', intensity: 0, concerns: ['wrinkles'], re: /\bpdrn\b|polynucleotide|سالمون/ },
    { family: 'adenosine', intensity: 0, concerns: ['wrinkles'], re: /adenosine|ادينوسين/ }
  ];

  const ACTIVE_INTENSITY = ACTIVE_LEXICON.reduce(function (map, item) {
    map[item.family] = item.intensity;
    return map;
  }, {});

  /**
   * Supporting ingredients. Detected in product descriptions as well as
   * titles, because ingredient names are factual rather than persuasive.
   */
  const INGREDIENT_LEXICON = [
    { key: 'hyaluronic', roles: ['hydrating'], concerns: ['dryness'], re: /hyaluron|هيالورون/ },
    { key: 'ceramide', roles: ['hydrating', 'barrier'], concerns: ['dryness'], re: /ceramide|سيراميد/ },
    { key: 'panthenol', roles: ['soothing', 'barrier'], concerns: ['dryness'], re: /panthenol|بانثينول|بانتينول/ },
    { key: 'squalane', roles: ['hydrating'], concerns: ['dryness'], re: /squalane|سكوالين/ },
    { key: 'beta-glucan', roles: ['hydrating'], concerns: ['dryness'], re: /beta.?glucan|بيتا.?جلوكان/ },
    { key: 'snail', roles: ['hydrating'], concerns: ['dryness'], re: /snail|حلزون|mucin|ميوسين/ },
    { key: 'glycerin', roles: ['hydrating'], concerns: ['dryness'], re: /glycerin|جلسرين|جليسرين/ },
    { key: 'urea', roles: ['hydrating'], concerns: ['dryness'], re: /\burea\b|يوريا/ },
    { key: 'centella', roles: ['soothing', 'barrier'], concerns: ['acne', 'dryness'], re: /centella|\bcica\b|madecass|سينتيلا|سنتيلا|سيكا|ماديكاسو/ },
    { key: 'heartleaf', roles: ['soothing'], concerns: ['acne', 'pores'], re: /heartleaf|houttuynia|هارتليف|هارت\s*ليف/ },
    { key: 'mugwort', roles: ['soothing'], concerns: ['acne'], re: /mugwort|artemisia|الشيح|ارتيميسيا/ },
    { key: 'propolis', roles: ['soothing'], concerns: ['acne'], re: /propolis|بروبوليس/ },
    { key: 'green-tea', roles: ['soothing', 'sebum-control'], concerns: ['pores', 'acne'], re: /green.?tea|camellia sinensis|الشاي\s*(?:ال)?اخضر/ },
    { key: 'aloe', roles: ['soothing'], concerns: [], re: /\baloe\b|الوفيرا|صبار/ },
    { key: 'allantoin', roles: ['soothing'], concerns: [], re: /allantoin|الانتوين/ },
    { key: 'zinc', roles: ['sebum-control'], concerns: ['pores'], re: /\bzinc\b|اكسيد\s*(?:ال)?زنك/ },
    { key: 'licorice', roles: ['brightening'], concerns: ['pigmentation'], re: /licorice|glycyrrhiza|عرق\s*(?:ال)?سوس/ },
    { key: 'rice', roles: ['brightening'], concerns: ['pigmentation'], re: /rice\s*(?:extract|ferment|water|bran)|(?:ال)?ارز(?=\s|$)/ },
    { key: 'collagen', roles: [], concerns: ['wrinkles'], re: /collagen|كولاجين|كلاجين/ }
  ];

  /**
   * Texture and finish claims. Name-scope only: product descriptions in this
   * catalog name every skin type and both textures in the same paragraph, so
   * reading claims from marketing copy produces near-universal matches.
   */
  const CLAIM_PATTERNS = {
    lightweight: /خفيف|lightweight|سريع\s*(?:ال)?امتصاص|fast\s*absor|watery|منعش|مائي|\bfluid\b|\bmilk\b|حليب/,
    rich: /غني|\brich\b|balm|زبده|nourish|مغذي|كثيف|shea|butter/,
    matte: /matte|مطفي|oil.?control|تحكم.*(?:زيوت|دهون)|sebum|زهم/,
    'oil-free': /oil.?free|non.?comedogenic|خالي\s*من\s*(?:ال)?زيوت|غير\s*دهني|لا\s*يسد\s*(?:ال)?مسام/
  };
  // Your store concern collections (not the English pore-care / acne-soothing set)
  const CONCERN_COLLECTION_FALLBACKS = {
    pores: ['مسام'],
    acne: ['حبوب'],
    pigmentation: ['تصبغات'],
    dryness: ['جفاف-وتقشر'],
    wrinkles: ['تجاعيد']
  };
  const COLLECTION_CONCERN_MAP = [
    { concern: 'pores', re: /^(مسام)$|pore-care|pore|مسام|sebum|رؤوس.?سوداء/ },
    { concern: 'acne', re: /^(حبوب)$|acne-soothing|\bacne\b|حبوب|بثور|تهيج/ },
    { concern: 'pigmentation', re: /^(تصبغات)$|brightening|تصبغ|تفتيح|بقع|dark.?spot/ },
    { concern: 'dryness', re: /جفاف-وتقشر|^hydration$|hydrat|جفاف|تقشر|barrier|حاجز/ },
    { concern: 'wrinkles', re: /^(تجاعيد)$|anti-aging|aging|wrinkle|تجاعيد|شد|lifting/ }
  ];

  const state = {
    panel: 'landing',
    skin: null,
    concern: null,
    products: [],
    routine: [],
    /** Phase C: full alternative list built once per skin×concern */
    routineList: [],
    routineIndex: 0,
    routineListKey: null,
    selectedSteps: {},
    swappingStep: null,
    loadingCatalog: false,
    catalogReady: false,
    loadedHandles: {},
    allProductsLoaded: false,
    discountApplied: false,
    intelReady: false,
    lastBuildMs: 0
  };

  const CONCERN_WHY_AR = {
    pores: 'مظهر المسام',
    pigmentation: 'مظهر التصبغات',
    acne: 'مظهر الحبوب',
    dryness: 'الترطيب والجفاف',
    wrinkles: 'مظهر الخطوط'
  };
  const CLINICAL_FAMILIES = [
    'retinoid',
    'exfoliating-acid',
    'salicylic',
    'niacinamide',
    'vitamin-c',
    'tranexamic',
    'azelaic',
    'arbutin',
    'kojic',
    'clay',
    'tea-tree',
    'bakuchiol',
    'peptide',
    'pdrn',
    'benzoyl'
  ];
  const ROUTINE_LIST_MAX = 10;
  const ANCHOR_CAP = 8;
  const SUPPORT_TIER_K = 3;

  /** Compressed sondos_intel asset (lazy). Null until ensureIntel(). */
  let intelPayload = null;
  let intelPromise = null;

  /** Quiz concern ← intel concern labels (plan §3ب). */
  const INTEL_CONCERN_ALIASES = {
    pores: ['pores', 'oil control', 'blackheads', 'texture', 'sebum', 'oil'],
    pigmentation: [
      'pigmentation',
      'dark spots',
      'hyperpigmentation',
      'uneven tone',
      'dullness',
      'brightening'
    ],
    acne: ['acne', 'blemish', 'pimples'],
    dryness: ['dryness', 'dehydration', 'barrier', 'barrier repair', 'dehydrated'],
    wrinkles: ['wrinkles', 'fine lines', 'anti-aging', 'aging', 'elasticity']
  };

  /** Tight description signals — used only to soften negative intel values. */
  const DESC_SOFTEN = {
    acneSafeYes: /non[-\s]?comedogenic|does not clog pores|won'?t clog pores|لا يسد(?: ال)?مسام|ما بسد(?: ال)?مسام|غير كوميدوجين|oil[-\s]?free|خالي من(?: ال)?زيوت|for acne[-\s]?prone|للبشرة المعرضه للحبوب/,
    sensitiveYes: /for sensitive skin|suitable for sensitive|للبشرة الحساسه|مناسب.?للحساسه|hypoallergenic|fragrance[-\s]?free|بدون عطر|خالي من العطر|صيغة لطيفه|gentle formula/,
    potencyGentle: /gentle daily|لطيف للاستخدام اليومي|mild cleanser|for all skin types/,
    textureGel: /water\s*gel|jelly cream|gel cream|جل مائي|تركيبته? جل|قوام جل/,
    textureWatery: /watery|lightweight serum|fluid texture|قوام مائي|تركيبته? مائي|\bmist\b|sun serum/,
    textureCream: /rich cream|thick cream|كريم غني|قوام كريمي كثيف|nourishing cream|\bbalm\b/
  };

  function mapIntelConcernToQuiz(label) {
    const n = normalize(label);
    if (!n) return null;
    const keys = Object.keys(INTEL_CONCERN_ALIASES);
    for (let i = 0; i < keys.length; i++) {
      const quiz = keys[i];
      const aliases = INTEL_CONCERN_ALIASES[quiz];
      for (let j = 0; j < aliases.length; j++) {
        const a = aliases[j];
        if (n === a || n.indexOf(a) !== -1 || a.indexOf(n) !== -1) return quiz;
      }
    }
    return null;
  }

  function decodeIntelRow(row) {
    if (!row || typeof row !== 'object') return null;
    const actives = Array.isArray(row.ac) ? row.ac.slice() : [];
    const activeBlob = actives.join(' ');
    // Cache family detection on the short actives string (avoid re-scanning body_html).
    const activeFamilies = activeBlob ? inferActiveFamilies(normalize(activeBlob)) : [];
    return {
      id: row.i || null,
      step: row.st || null,
      skins: Array.isArray(row.sk) ? row.sk.slice() : [],
      concernEffects: Array.isArray(row.ce)
        ? row.ce
            .map(function (pair) {
              const concern = pair && pair[0] ? String(pair[0]) : '';
              const strength = pair && pair[1] === 'P' ? 'Primary Treatment' : 'Secondary Support';
              return { concern: concern, strength: strength, quiz: mapIntelConcernToQuiz(concern) };
            })
            .filter(function (x) {
              return x.concern;
            })
        : [],
      actives: actives,
      activeFamilies: activeFamilies,
      potency: row.po === 'G' ? 'gentle' : row.po === 'A' ? 'active' : null,
      texture: row.tx || null,
      acneSafe: row.as === 'Y' ? true : row.as === 'N' ? false : null,
      sensitiveSafe: row.ss === 'Y' ? true : row.ss === 'N' ? false : null,
      excluded: row.ex === 1,
      brand: row.br || null
    };
  }

  function lookupIntel(product) {
    if (!intelPayload || !product) return null;
    const byHandle = intelPayload.h || {};
    const byId = intelPayload.i || {};
    let row = null;
    if (product.handle && byHandle[product.handle]) {
      row = byHandle[product.handle];
    } else if (product.id != null && byId[String(product.id)]) {
      row = byHandle[byId[String(product.id)]] || null;
    }
    return decodeIntelRow(row);
  }

  /**
   * Metadata-first merge: intel wins per field; NLP/tags fill empties only.
   * Negative values may soften to Unknown via description checks (not hard delete).
   */
  function applyIntelToMeta(meta, intel, product) {
    if (!meta || !intel) return meta;
    const conflicts = [];
    meta.intel = intel;
    meta.fromIntel = true;

    // Metadata-first: intel Exclude No must clear NLP/title false positives
    // (e.g. centella ampoule tagged/misread as non-routine).
    if (typeof intel.excluded === 'boolean') meta.excluded = intel.excluded;
    else if (intel.excluded) meta.excluded = true;

    if (intel.step) {
      meta.step = intel.step;
      meta.stepConf = 1;
      meta.stepSource = 'intel';
    }

    if (intel.skins && intel.skins.length) {
      meta.skins = intel.skins.slice();
      meta.skinSource = 'intel';
      meta.universal =
        meta.skins.indexOf('normal') !== -1 &&
        meta.skins.length >= 4;
    }

    if (intel.concernEffects && intel.concernEffects.length) {
      const quizConcerns = [];
      const effects = [];
      intel.concernEffects.forEach(function (e) {
        if (!e.quiz) return;
        if (quizConcerns.indexOf(e.quiz) === -1) quizConcerns.push(e.quiz);
        effects.push({
          quiz: e.quiz,
          concern: e.concern,
          strength: e.strength,
          fit: e.strength === 'Primary Treatment' ? 1 : 0.55
        });
      });
      if (quizConcerns.length) {
        meta.concerns = quizConcerns;
        meta.intelConcernEffects = effects;
        meta.concernSource = 'intel';
      }
    }

    if (intel.texture) {
      meta.texture = intel.texture;
      meta.textureSource = 'intel';
    }

    if (intel.potency) {
      meta.strength = intel.potency;
      meta.strengthSource = 'intel';
    }

    meta.acneSafe = intel.acneSafe;
    meta.sensitiveSafe = intel.sensitiveSafe;

    if (intel.activeFamilies && intel.activeFamilies.length) {
      meta.activeFamilies = intel.activeFamilies.slice();
      meta.nameActives = intel.activeFamilies.slice();
      meta.activesSource = 'intel';
    }

    if (intel.actives && intel.actives.length) {
      const roles = inferSupportRoles(
        normalize((product && product.title) || ''),
        normalize(intel.actives.join(' '))
      );
      if (roles.length) meta.supportRoles = roles;
    }

    if (intel.brand) meta.brand = intel.brand;

    // Soften negatives from description — never hard-delete for card mismatch.
    const bodyText = (product && (product.bodyText || (product.bodyHtml ? plainText(product.bodyHtml) : ''))) || '';
    const blob = normalize([bodyText, (product && product.title) || '', (product && product.handle) || ''].join(' '));
    if (meta.acneSafe === false && DESC_SOFTEN.acneSafeYes.test(blob)) {
      meta.acneSafe = null;
      conflicts.push('acne_safe:desc-unknown');
    }
    if (meta.sensitiveSafe === false && DESC_SOFTEN.sensitiveYes.test(blob)) {
      meta.sensitiveSafe = null;
      conflicts.push('sensitive_safe:desc-unknown');
    }
    if (meta.strength === 'active' && DESC_SOFTEN.potencyGentle.test(blob) && !(meta.nameActives || []).length) {
      meta.strength = null;
      conflicts.push('potency:desc-unknown');
    }
    if (meta.texture === 'cream' && (DESC_SOFTEN.textureGel.test(blob) || DESC_SOFTEN.textureWatery.test(blob))) {
      if (DESC_SOFTEN.textureGel.test(blob)) meta.texture = 'gel';
      else meta.texture = 'watery';
      conflicts.push('texture:desc-override');
    }

    meta.intelConflicts = conflicts;
    // Confidence: intel clinical card is stronger than tags/title alone.
    meta.confidence = Math.min(1, Math.max(meta.confidence || 0.35, 0.82 + (conflicts.length ? -0.08 : 0.06)));
    return meta;
  }

  /**
   * Load arb-intel.json once. Called on quiz start / catalog — never on homepage
   * LCP path (JS itself is already IntersectionObserver / click-gated).
   */
  function ensureIntel() {
    if (intelPayload) return Promise.resolve(intelPayload);
    if (intelPromise) return intelPromise;
    const url = boot.intelUrl;
    if (!url) {
      intelPayload = { v: 1, h: {}, i: {} };
      state.intelReady = true;
      return Promise.resolve(intelPayload);
    }
    intelPromise = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('intel HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        intelPayload = data && typeof data === 'object' ? data : { v: 1, h: {}, i: {} };
        state.intelReady = true;
        return intelPayload;
      })
      .catch(function (err) {
        console.warn('[AI Routine] intel load failed', err);
        intelPayload = { v: 1, h: {}, i: {} };
        state.intelReady = true;
        return intelPayload;
      })
      .finally(function () {
        intelPromise = null;
      });
    return intelPromise;
  }


  const panels = {
    landing: ui.querySelector('[data-arb-panel="landing"]'),
    skin: ui.querySelector('[data-arb-panel="skin"]'),
    concern: ui.querySelector('[data-arb-panel="concern"]'),
    results: ui.querySelector('[data-arb-panel="results"]')
  };

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[أإآ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Strip a Shopify body_html down to searchable text. Only ingredient names
   * are read from descriptions (see INGREDIENT_LEXICON), so the output is
   * capped: nothing useful lives past a few thousand characters.
   */
  function plainText(html) {
    return String(html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|&amp;|&quot;|&#\d+;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 6000);
  }

  // Display only — no selection/scoring behaviour depends on this.
  // Wave 1 / F-01: mirrors snippets/sondos-money.liquid exactly, so a routine
  // total reads the same as the same product's price on a card: NIS before the
  // number, no trailing .00, real fractions kept. Replaces Shopify.formatMoney,
  // which rendered the shop format ("72.00 ILS") and disagreed with the cards.
  function formatMoney(cents) {
    let n = Number(cents) / 100;
    if (!isFinite(n)) n = 0;
    const negative = n < 0;
    const rounded = Math.round(Math.abs(n) * 100) / 100;
    const body = rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(2);
    const parts = body.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (negative ? '-' : '') + 'NIS ' + parts.join('.');
  }

  function renderPriceBlock(product) {
    const price = Number(product.price || 0);
    const compareAt = Number(product.compareAt || 0);
    if (!(compareAt > price)) {
      return (
        '<div class="arb__item-price">' +
          '<div class="sondos-pdp-price sondos-pdp-price--compact">' +
            '<div class="sondos-pdp-price__row">' +
              '<span class="price-item price-item--sale sondos-pdp-price__sale sondos-money" dir="ltr">' +
                escapeHtml(formatMoney(price)) +
              '</span>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }
    return (
      '<div class="arb__item-price">' +
        '<div class="sondos-pdp-price sondos-pdp-price--compact">' +
          '<div class="sondos-pdp-price__row">' +
            '<s class="price-item price-item--regular sondos-pdp-price__compare sondos-money" dir="ltr">' +
              escapeHtml(formatMoney(compareAt)) +
            '</s>' +
            '<span class="price-item price-item--sale sondos-pdp-price__sale sondos-money" dir="ltr">' +
              escapeHtml(formatMoney(price)) +
            '</span>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function showPanel(name) {
    state.panel = name;
    Object.keys(panels).forEach((key) => {
      const el = panels[key];
      if (!el) return;
      const on = key === name;
      el.hidden = !on;
      el.classList.toggle('is-active', on);
    });
    const map = { landing: 1, skin: 2, concern: 3, results: 4 };
    const current = map[name] || 1;
    const canGo = {
      1: true,
      2: true, // same entry as «ابدئي الآن»
      3: !!state.skin,
      4: isRoutineReady(state.routine)
    };
    ui.querySelectorAll('[data-step-dot]').forEach((dot) => {
      const n = Number(dot.getAttribute('data-step-dot'));
      const nav = !!canGo[n];
      dot.classList.toggle('is-active', n === current);
      dot.classList.toggle('is-done', n < current);
      dot.classList.toggle('is-nav', nav);
      dot.setAttribute('aria-disabled', nav ? 'false' : 'true');
      dot.setAttribute('tabindex', nav ? '0' : '-1');
    });
  }

  function startQuizFlow() {
    // Prefetch intel after the shopper opens the quiz — not on homepage LCP.
    ensureIntel();
    ensureCatalog(state.skin, state.concern);
    showPanel('skin');
  }

  function goToProgressStep(step) {
    const n = Number(step);
    if (n === 1) {
      showPanel('landing');
      return;
    }
    if (n === 2) {
      startQuizFlow();
      return;
    }
    if (n === 3) {
      if (!state.skin) return;
      showPanel('concern');
      return;
    }
    if (n === 4) {
      if (!isRoutineReady(state.routine)) return;
      showPanel('results');
    }
  }

  function parseLegacyTag(tag) {
    const t = normalize(tag).replace(/_/g, ' ').replace(/\s+/g, ' ');
    const out = { skins: [], concerns: [] };
    const skinKeys = ['oily', 'dry', 'combination', 'sensitive', 'normal'];
    skinKeys.forEach((sk) => {
      if (t.includes(sk) || t.startsWith(sk + '-') || t.includes(sk + ' ')) out.skins.push(sk);
    });

    // Strict concern mapping from structured/legacy tags only (NOT generic "ترطيب")
    if (/dark-spot|darkspot|pigment/.test(t)) out.concerns.push('pigmentation');
    if (/anti-aging|wrinkle|aging/.test(t)) out.concerns.push('wrinkles');
    if (/\bacne\b|oily-acne/.test(t)) out.concerns.push('acne');
    if (/\bpore/.test(t)) out.concerns.push('pores');
    // hydration legacy tag ≈ dryness concern (specific tag, not Arabic "ترطيب" everywhere)
    if (/\bhydration\b|\bdryness\b|dry hydration/.test(t)) out.concerns.push('dryness');

    return out;
  }

  function normalizeHandle(handle) {
    return String(handle || '').trim();
  }

  function handleKey(handle) {
    return normalizeHandle(handle).toLowerCase();
  }

  function concernFromCollectionHandle(handle) {
    const key = handleKey(handle);
    if (!key) return null;
    for (let i = 0; i < COLLECTION_CONCERN_MAP.length; i++) {
      if (COLLECTION_CONCERN_MAP[i].re.test(key)) return COLLECTION_CONCERN_MAP[i].concern;
    }
    return null;
  }

  function resolveConcernCollectionHandles(concern) {
    const out = [];
    const fromBoot = boot.concernCollections && boot.concernCollections[concern];
    if (fromBoot) out.push(fromBoot);
    const fallback = CONCERN_COLLECTION_FALLBACKS[concern] || [];
    fallback.forEach((h) => out.push(h));
    const seen = {};
    const unique = [];
    out.forEach((h) => {
      const raw = normalizeHandle(h);
      const key = handleKey(raw);
      if (!raw || seen[key]) return;
      seen[key] = true;
      unique.push(raw);
    });
    return unique;
  }

  function isInConcernCollections(product, concern) {
    if (!product || !product.meta) return false;
    const wanted = resolveConcernCollectionHandles(concern).map(handleKey);
    if (!wanted.length) return false;
    const source = (product.meta.sourceCollections || []).map(handleKey);
    for (let i = 0; i < source.length; i++) {
      if (wanted.indexOf(source[i]) !== -1) return true;
    }
    return false;
  }

  // Real pore signals — NOT bare "جيجو" (matches "جزيرة جيجو" orchid cream by mistake)
  const PORE_TITLE_RE =
    /تصغير المسام|للمسام|تنقية المسام|مسام|pore|sebum|زهم|blackhead|رؤوس سوداء|oil control|طين بركان|بركان|volcanic|volcan|جيجو فولكانيك|jeju volcanic|غسول الطين|clay cleanser/;

  /** Moisturizer: collection is source of truth. Cleanser/toner: collection or title signal. */
  function fitsConcernStep(product, concern, step) {
    if (!product || !product.meta) return false;
    if (isInConcernCollections(product, concern)) return true;
    if (step === 'moisturizer') {
      // Don't trust noisy title/tags for cream — wrong SKUs (orchid/Jeju island) leaked in
      return false;
    }
    if ((product.meta.concerns || []).indexOf(concern) !== -1) return true;
    if (step === 'cleanser' || step === 'toner') {
      const t = normalize(product.title || '');
      if (concern === 'pores' && PORE_TITLE_RE.test(t)) return true;
      if (concern === 'acne' && /ازيليك|azelaic|حبوب|acne|blemish|عيوب/.test(t)) return true;
      if (concern === 'pigmentation' && /تصبغ|بقع|تفتيح|bright|dark.?spot/.test(t)) return true;
      if (concern === 'dryness' && /جفاف|ترطيب|هيالورون|hyaluron|ceramide|سيراميد/.test(t)) return true;
      if (concern === 'wrinkles' && /تجاعيد|خطوط|retinol|peptide|ببتيد|كولاجين/.test(t)) return true;
    }
    return false;
  }

  const SKIP_BRAND_TAGS = {
    skincare: 1, new: 1, new2: 1, package: 1, mask: 1, makeup: 1, bodyhair: 1, body: 1,
    hair: 1, soon: 1, good: 1, bestseller: 1, 'best-seller': 1, brightening: 1
  };

  function productBrand(product) {
    const tags = product.tags || [];
    for (let i = 0; i < tags.length; i++) {
      const raw = String(tags[i] || '');
      const key = normalize(raw);
      if (!key || SKIP_BRAND_TAGS[key]) continue;
      if (key.indexOf('skin-') === 0 || key.indexOf('concern-') === 0 || key.indexOf('step-') === 0) continue;
      if (key.indexOf('texture-') === 0 || key.indexOf('strength-') === 0) continue;
      if (/oily|dry|combination|sensitive|normal|acne|pore|dark|hydrat|anti-aging/.test(key)) continue;
      return key;
    }
    return normalize(product.vendor || '') || 'unknown';
  }

  /** True brand family from title/handle — never treat store vendor as a brand */
  function brandFamily(product) {
    const text = normalize([product && product.title, product && product.handle].join(' '));
    if (/anua|انوا/.test(text)) return 'anua';
    if (/1004|سكن 1004|skin.?1004|madagascar/.test(text)) return 'skin1004';
    if (/celimax|سيليماكس|سيلماكس/.test(text)) return 'celimax';
    if (/medicube|ميديكيوب|ميديكوب/.test(text)) return 'medicube';
    if (/beauty of joseon|بيوتي اوف جوسون|جوسون|\bboj\b/.test(text)) return 'boj';
    if (/cosrx|كوزركس/.test(text)) return 'cosrx';
    if (/purito|بوريتو|بيوريتو/.test(text)) return 'purito';
    if (/innisfree|انيسفري|اينسفري/.test(text)) return 'innisfree';
    if (/etude|ايتود/.test(text)) return 'etude';
    if (/missha|ميشا/.test(text)) return 'missha';
    if (/some by mi|سوم باي مي/.test(text)) return 'sbm';
    if (/goodal|جودال/.test(text)) return 'goodal';
    if (/dr\.?\s*althea|دكتور الثيا|الثيا/.test(text)) return 'althea';
    if (/axis.?y|اكسس واي|اكسس/.test(text)) return 'axisy';
    if (/torriden|توريدين/.test(text)) return 'torriden';
    if (/arencia|ارنسيا|ارينسيا|ارنيسيا|أرنسيا/.test(text)) return 'arencia';
    if (/i'?m\s*from|ايم فروم|ام فروم/.test(text)) return 'imfrom';
    if (/mixsoon|ميكسون/.test(text)) return 'mixsoon';
    if (/round.?lab|راوند/.test(text)) return 'roundlab';
    if (/biodance|بيودانس/.test(text)) return 'biodance';
    if (/ionic|آيونيك|ايونيك/.test(text)) return 'ionic';
    if (/skin food|سكين فود/.test(text)) return 'skinfood';
    if (/haruharu|هاروه/.test(text)) return 'haruharu';
    if (/apeu|ابيو|أبيو/.test(text)) return 'apeu';
    // Tag/vendor fallback only for real brand tags — ignore store name
    const brand = normalize((product && product.meta && product.meta.brand) || productBrand(product) || '');
    if (!brand || /sondos|store|unknown|skincare|new\d*|package|mask/.test(brand)) return 'unknown';
    return brand;
  }

  function isRealBrand(brand) {
    return !!(brand && brand !== 'unknown' && !/sondos|store/.test(brand));
  }

  function preferBrandPool(stepPool, preferBrand) {
    if (!preferBrand || !stepPool.length) return stepPool;
    const same = stepPool.filter((p) => brandFamily(p) === preferBrand);
    return same.length ? same : stepPool;
  }

  function productBrandKey(product) {
    return product ? brandFamily(product) : 'unknown';
  }

  /**
   * Hard skin gate, reserved for combinations that would genuinely harm or
   * clearly fail the customer.
   *
   * Previously this gate excluded any product whose skin label didn't match,
   * which meant labelling a product could only ever make it unreachable — an
   * untagged product outranked a correctly tagged one purely by surviving.
   * Softer mismatches are now priced by skinAffinity instead of blocked.
   */
  function skinCompatible(product, skin, step, concern) {
    if (!skin) return true;
    const meta = (product && product.meta) || {};
    const skins = meta.skins || [];
    const title = normalize(product && product.title);
    const gentle = meta.strength === 'gentle';
    const active = meta.strength === 'active';
    const leaveOn = step === 'moisturizer' || step === 'sunscreen';
    const labelled = (type) => skins.indexOf(type) !== -1;

    if (!skins.length || meta.universal || labelled(skin)) return true;

    if (skin === 'sensitive') {
      // Intel sensitive_safe=Yes keeps the product reachable; score soft-penalizes actives.
      if (meta.sensitiveSafe === true) return true;
      if (active) return false;
      return gentle || labelled('dry') || labelled('normal');
    }
    if (skin === 'oily') {
      // An occlusive leave-on built for dry skin feels greasy whatever else it offers.
      const occlusive = meta.texture === 'cream' || CLAIM_PATTERNS.rich.test(title);
      if (leaveOn && occlusive && labelled('dry') && !labelled('combination')) return false;
      return true;
    }
    if (skin === 'dry') {
      // Potent oil-stripping formulas aimed only at oily skin.
      if (active && !gentle && labelled('oily') && !labelled('dry') && !labelled('sensitive')) return false;
      return true;
    }
    return true;
  }

  /**
   * Graded skin fit in roughly -0.75 .. 1. A confirmed label match scores 1,
   * an unlabelled product a deliberately modest SKIN_AFFINITY_UNKNOWN, and an
   * opposite label goes negative. Ordering matters more than the exact numbers:
   * proven match > unknown > proven mismatch.
   */
  function skinAffinity(product, skin, step, concern) {
    const meta = (product && product.meta) || {};
    const skins = meta.skins || [];
    const row = SKIN_AFFINITY[skin] || {};
    if (!skins.length) return SKIN_AFFINITY_UNKNOWN;
    // Oily skin asking about dehydration is the one case where a dry-skin label
    // is informative rather than disqualifying — as long as it is a lightweight
    // treatment step and not an occlusive cream, which isHardEligible still blocks.
    const dryLabelIsRelevant =
      skin === 'oily' && concern === 'dryness' && (step === 'serum' || step === 'toner');
    let best = -1;
    let worst = 1;
    skins.forEach((label) => {
      let value = typeof row[label] === 'number' ? row[label] : 0;
      if (dryLabelIsRelevant && label === 'dry') value = 0.15;
      if (value > best) best = value;
      if (value < worst) worst = value;
    });
    // A favourable label must not cancel an unfavourable one. A sunscreen
    // tagged for both dry and sensitive skin still carries the dry-skin
    // warning when the customer is oily; taking the best label alone made that
    // warning disappear.
    if (worst < 0) best += 0.5 * worst;
    if (meta.universal && best < row.normal) best = row.normal || 0.3;
    return best;
  }

  /**
   * Graded concern relevance in 0..1, accumulated from independent sources so
   * that agreement between them is what makes a product a strong match. The
   * old model was binary — one tag meant +90, no tag meant -90 — which let a
   * single missing tag hide a well-suited product completely.
   */
  function concernEvidence(product, concern) {
    const meta = (product && product.meta) || {};
    // Metadata-first: intel concern_effects dominate when present.
    if (meta.intelConcernEffects && meta.intelConcernEffects.length) {
      let best = 0;
      const sources = [];
      meta.intelConcernEffects.forEach(function (e) {
        if (e.quiz === concern) {
          if (e.fit > best) best = e.fit;
          if (sources.indexOf('intel') === -1) sources.push('intel');
        } else if ((RELATED_CONCERNS[concern] || []).indexOf(e.quiz) !== -1) {
          if (best < 0.15) best = 0.15;
          if (sources.indexOf('intel-related') === -1) sources.push('intel-related');
        }
      });
      // Tiny confirmation from the product name — never overrides intel rank.
      if ((meta.titleConcerns || []).indexOf(concern) !== -1) {
        best = Math.min(1, best + 0.05);
        sources.push('name');
      }
      return { fit: Math.min(1, best), sources: sources };
    }

    let fit = 0;
    const sources = [];
    // What the product calls itself is the strongest single signal: it reflects
    // the formulator's primary intent, not a tag or a blurb.
    if ((meta.titleConcerns || []).indexOf(concern) !== -1) {
      fit += 0.46;
      sources.push('name');
    }
    if ((meta.tagConcerns || []).indexOf(concern) !== -1) {
      fit += 0.32;
      sources.push('tag');
    }
    if ((meta.concernCollections || []).indexOf(concern) !== -1) {
      fit += 0.22;
      sources.push('collection');
    }
    const formula = (meta.formulaConcerns || {})[concern] || 0;
    if (formula > 0) {
      // Capped: a long ingredient list must not out-argue the product's purpose.
      fit += Math.min(0.24, 0.06 * formula);
      sources.push('formula');
    }
    if ((RELATED_CONCERNS[concern] || []).some((c) => (meta.concerns || []).indexOf(c) !== -1)) {
      fit += 0.1;
      sources.push('related');
    }
    return { fit: Math.min(1, fit), sources: sources };
  }

  /**
   * Base steps (cleanser / moisturizer / sunscreen): wide pool.
   * Treatment steps (toner / serum): concern-first accuracy.
   * Scoring decides rank — we do NOT collapse to 2–3 SKUs via hard filters.
   */
  /** Minimum eligible products a step should keep before relevance filtering stops. */
  const POOL_FLOOR = 6;

  function buildStepPool(allPool, step, skin, concern) {
    const stepAll = allPool.filter((p) => isHardEligible(p, step, skin, concern));
    if (step !== 'serum' && step !== 'toner') return stepAll;

    // The serum is the treatment: it must address the concern, full stop. This
    // is the one place a hard relevance filter earns its keep.
    if (step === 'serum') {
      const supported = stepAll.filter((p) => serumSupportsConcern(p, concern));
      return supported.length ? supported : stepAll;
    }

    // The toner supports rather than treats, so relevance here is a preference.
    // The old cascade returned the first non-empty tier, which could collapse
    // the toner shelf to a single SKU and make rotation impossible. Keep the
    // relevant products plus enough runners-up that selection still has room;
    // ranking decides the winner.
    const relevant = stepAll.filter((p) => concernEvidence(p, concern).fit >= TREATMENT_FIT_FLOOR);
    if (relevant.length >= POOL_FLOOR || relevant.length === stepAll.length) return relevant;

    const rest = stepAll
      .filter((p) => relevant.indexOf(p) === -1)
      .sort((a, b) => concernEvidence(b, concern).fit - concernEvidence(a, concern).fit);
    return relevant.concat(rest.slice(0, POOL_FLOOR - relevant.length));
  }

  function isHardEligible(product, step, skin, concern) {
    if (!product || !product.meta || product.meta.excluded) return false;
    if (!product.available || !product.variantId || product.meta.step !== step) return false;
    if (!skinCompatible(product, skin, step, concern)) return false;

    const title = normalize(product.title || '');
    const meta = product.meta;
    const nameActives = meta.nameActives || meta.activeFamilies || [];
    // Moisturizer hard gate: only real treatment actives (intensity > 0).
    // Intensity-0 families (peptide, TXA, …) must not wipe light gels (e.g. BOJ Red Bean).
    const treatmentActives = nameActives.filter(function (family) {
      return (ACTIVE_INTENSITY[family] || 0) > 0;
    });
    if (
      step === 'moisturizer' &&
      treatmentActives.length &&
      !treatmentActives.some((family) => activeFamilySupportsConcern(family, concern))
    ) {
      return false;
    }
    if (
      step === 'moisturizer' &&
      (skin === 'oily' || skin === 'combination') &&
      meta.texture === 'cream' &&
      !oilyCreamMoisturizerAllowed(product)
    ) {
      return false;
    }
    if (
      concern === 'dryness' &&
      (step === 'serum' || step === 'toner') &&
      nameActives.some(
        (family) =>
          family === 'retinoid' ||
          family === 'exfoliating-acid' ||
          family === 'salicylic' ||
          family === 'clay' ||
          family === 'benzoyl' ||
          family === 'azelaic'
      )
    ) {
      return false;
    }
    if (skin === 'sensitive') {
      const harshOnSensitive = nameActives.some(function (family) {
        return (
          family === 'retinoid' ||
          family === 'exfoliating-acid' ||
          family === 'salicylic' ||
          family === 'benzoyl'
        );
      });
      if (meta.strength === 'active') {
        // Sensitive×acne was stuck at 1 serum (gentle niacinamide only).
        // Allow Active azelaic/niacinamide serums labeled for sensitive skin
        // when intel is not sensitiveSafe=No and no harsh acids/retinoids.
        // Tea-tree + azelaic (Purito) is allowed for acne — score soft-penalizes.
        // Active leave-ons only for sensitive×acne (azelaic soothing paths).
        // sensitive×pores stays gentle — blocks Anua Peach Active (QA sensitive-active).
        const allowSensitiveAcneSerum =
          step === 'serum' &&
          concern === 'acne' &&
          meta.skins.indexOf('sensitive') !== -1 &&
          meta.sensitiveSafe !== false &&
          !harshOnSensitive &&
          nameActives.some(function (family) {
            return family === 'azelaic' || family === 'niacinamide';
          });
        if (!allowSensitiveAcneSerum) return false;
      }
      const explicitlySensitive = meta.skins.indexOf('sensitive') !== -1;
      const safelyGentle =
        meta.strength === 'gentle' ||
        /مهدئ|لطيف|soothing|gentle|centella|سينتيلا|heartleaf|هارتليف/.test(title);
      if (meta.skins.length && !meta.universal && !explicitlySensitive && !safelyGentle) return false;
      if (step === 'cleanser' && /عميق|deep clean|طين|clay|volcan|بركان|salicylic|ساليسيليك|\bbha\b/.test(title)) {
        return false;
      }
    }
    if (
      skin === 'dry' &&
      step === 'cleanser' &&
      /عميق|deep clean|oil.?control|sebum|طين|clay|volcan|بركان|salicylic|ساليسيليك|bha/.test(title)
    ) {
      return false;
    }
    if (
      step === 'cleanser' &&
      concern !== 'pores' &&
      concern !== 'acne' &&
      /عميق|deep clean|طين|clay|volcan|بركان|salicylic|ساليسيليك|\bbha\b/.test(title)
    ) {
      return false;
    }
    if (step === 'sunscreen') {
      if (/tinted|tone.?up|ملون|مع لون|sun.?stick|ستيك/.test(title)) return false;
      if (skin === 'sensitive' && meta.strength === 'active') return false;
      if (skin === 'oily' && concern === 'pores') {
        const oilyPoreFit =
          meta.skins.indexOf('oily') !== -1 ||
          meta.concerns.indexOf('pores') !== -1 ||
          /oil.?free|non.?comedogenic|lightweight|matte|oil.?control|sebum|خفيف|غير دهني|تحكم.*زيوت|مسام|pore/.test(title);
        const differentFocus =
          meta.concerns.indexOf('wrinkles') !== -1 ||
          (
            meta.concerns.indexOf('pigmentation') !== -1 &&
            meta.concerns.indexOf('pores') === -1
          ) ||
          /collagen|كولاجين|كلاجين|anti.?aging|شد البشرة|lifting/.test(title);
        if (differentFocus && !oilyPoreFit) return false;
      }
    }
    return true;
  }

  function isAcceptableBrandMatch(product, step, skin, concern) {
    if (!product || !product.meta) return false;
    if (step === 'moisturizer') {
      // Brand lock for cream only if it's actually in the concern collection
      return isInConcernCollections(product, concern);
    }
    if (concern === 'pores' && step === 'cleanser') {
      const t = normalize(product.title || '');
      if (/اوركيد|orchid|تجاعيد|anti.?aging/.test(t) && !PORE_TITLE_RE.test(t)) return false;
    }
    const scored = scoreProduct(product, step, skin, concern, {}, {});
    if (!isFinite(scored.score)) return false;
    if (step === 'sunscreen') return scored.score > -20;
    return scored.score > 15;
  }

  /**
   * Simple brand cascade:
   * toner/serum → same brand + concern, else open concern pool
   * moisturizer → same brand ONLY if in concern collection, else open pool
   * sunscreen → same brand if acceptable, else open pool
   */
  function cascadeBrandPool(allPool, step, skin, concern, brandChain) {
    const openPool = buildStepPool(allPool, step, skin, concern);
    // Brand is only a small tie-breaker in scoreProduct; it never narrows eligibility.
    return { pool: openPool, lockedBrand: null };
  }

  /** Keep shortlist brand-diverse so one hero SKU can't monopolize reshuffles */
  function diversifyByBrand(ranked, maxN, maxPerBrand) {
    maxPerBrand = maxPerBrand || 1;
    const out = [];
    const counts = {};
    ranked.forEach((r) => {
      if (out.length >= maxN) return;
      const b = brandFamily(r.product);
      if ((counts[b] || 0) >= maxPerBrand) return;
      counts[b] = (counts[b] || 0) + 1;
      out.push(r);
    });
    if (out.length < Math.min(4, ranked.length, maxN)) {
      ranked.forEach((r) => {
        if (out.length >= maxN) return;
        if (out.indexOf(r) !== -1) return;
        out.push(r);
      });
    }
    return out.length ? out : ranked.slice(0, maxN);
  }

  /**
   * Concern signals from the product NAME (title + handle) only — never from
   * long marketing blurbs, which name every concern at once. Uses the shared
   * CONCERN_PATTERNS so a term that earns a scoring bonus can also reach the
   * pool that bonus applies to.
   */
  function inferConcernsFromTitle(title, handle) {
    const t = normalize([title, handle].join(' '));
    const scores = { pores: 0, pigmentation: 0, acne: 0, dryness: 0, wrinkles: 0 };

    Object.keys(CONCERN_PATTERNS).forEach((concern) => {
      if (CONCERN_PATTERNS[concern].test(t)) scores[concern] += 3;
    });
    // Niacinamide genuinely serves both; keep the secondary signal explicit.
    if (/niacinamide|نياسيناميد/.test(t)) {
      scores.pores += 3;
      scores.pigmentation += 2;
    }

    // Keep top signals only (max 2) to avoid "everything" products
    return Object.keys(scores)
      .filter((k) => scores[k] >= 3)
      .sort((a, b) => scores[b] - scores[a])
      .slice(0, 2);
  }

  /** Hard exclusions: makeup, body/hair, sheet masks, kits, tools, lips, eyes, etc. */
  function isNonRoutineProduct(product) {
    const tags = (product.tags || []).map(normalize);
    const text = normalize([product.title, product.handle, ...(product.tags || [])].join(' '));

    const isTonerPad = /تونر|toner/.test(text) && /pad|قطن|قطنية|pads/.test(text);
    const isFacePorePad = /pore pad|zero pore|منظفات قطن|clear pad/.test(text) && !/جسم|body|شعر|hair/.test(text);

    const blockedTags = [
      'makeup', 'bodyhair', 'body', 'hair', 'mask', 'package', 'soon', 'ai-exclude',
      'lipstick', 'fragrance', 'perfume', 'tool'
    ];
    for (const t of blockedTags) {
      if (!tags.includes(t)) continue;
      if (t === 'mask' && (isTonerPad || isFacePorePad)) continue;
      return true;
    }

    if (/مكياج|makeup|cushion|foundation|كونسيلر|concealer|mascara|ماسكرا|eyeliner|lipstick|lip tint|تنت الخدود|تنت الشفا|بلشر|blush|contour|bronzer|كريم اساس|بي بي كريم|بيبي كريم|\bbb\b|\bcc cream\b|setting spray|باودر|powder compact/.test(text)) {
      return true;
    }
    // User preference: exclude oil cleansers, bar soaps, sunscreen sticks, and tinted SPF
    if (/oil cleanser|cleansing oil|غسول.*زيتي|زيتي.*غسول/.test(text)) return true;
    // Routines are built around gel/foam cleansers; bar soap is never the daily anchor.
    // Guarded so a "soap-free" claim in the title does not exclude a valid cleanser.
    if (/صابون|soap/.test(text) && !/خالي من الصابون|بدون صابون|soap.?free/.test(text)) return true;
    if (/sun\s*stick|sunstick|stick\s*sunscreen|واقي.*ستيك|ستيك.*واقي/.test(text)) return true;
    if (
      /واقي.?شمس.*(?:مع لون|ملون)|(?:مع لون|ملون).*واقي.?شمس|tinted.*(?:sun|spf)|(?:sun|spf).*tinted|tone.?up.*(?:sun|spf)|(?:sun|spf).*tone.?up/.test(
        text
      )
    ) {
      return true;
    }
    if (/ للجسم|للشعر|زيت الشعر|ماسك.*شعر|شعر من|shampoo|conditioner|body lotion|body wash|body peeling|ليفه|ليفة|مقشر جسم|مزيل عرق|deodorant|hair oil|hair mask|body mist|ميست الجسم|صابون جسم/.test(text)) {
      return true;
    }
    if (/\bbody\b|\bhair\b/.test(text) && !/face|وجه|بشرة/.test(text)) {
      if (/peeling pad|لوشن|lotion|oil|ماسك|mask|wash|shampoo|conditioner/.test(text)) return true;
    }
    if (/شيت\s*ماسك|sheet\s*mask|mask\s*pack|قناع ورقي|ماسك ورقي|hydrogel mask|sleeping mask|ماسك ليلي|ماسك الطين|clay mask|wash.?off mask|ماسك الشفا|lip sleeping|eye patch|شرائح العيون|باتش العين/.test(text)) {
      return true;
    }
    if (/ماسك|mask/.test(text) && !(isTonerPad || isFacePorePad)) return true;
    // Wash-off peeling gels are not daily leave-on toners (Phase E expert).
    if (/peeling gel|مقشر جل|wash[\s-]?off\s*gel/.test(text)) return true;
    // Kits only from PRODUCT title/handle — collection tags like "مجموعة الكرز/جيجو" must not kill single SKUs
    const titleHandle = normalize([product.title, product.handle].join(' '));
    if (/مجموعه|package|\bkit\b|\bset\b|bundle/.test(titleHandle)) return true;
    if (/كريم اليد|hand cream|كريم الشفا|lip balm|lip mask|كريم العين|كريم العيون|eye cream|eye serum|سيروم العين|سيرم العين|سيروم العيون|سيرم العيون|محيط العين/.test(text)) {
      return true;
    }
    return false;
  }

  function inferStep(product) {
    if (isNonRoutineProduct(product)) return { step: null, conf: 0 };

    const tags = (product.tags || []).map(normalize);
    for (const tag of tags) {
      if (tag === 'step-cleanser') return { step: 'cleanser', conf: 1 };
      if (tag === 'step-toner') return { step: 'toner', conf: 1 };
      if (tag === 'step-serum') return { step: 'serum', conf: 1 };
      if (tag === 'step-moisturizer') return { step: 'moisturizer', conf: 1 };
      if (tag === 'step-sunscreen') return { step: 'sunscreen', conf: 1 };
    }

    const text = normalize([product.title, product.handle, ...(product.tags || [])].join(' '));

    // Note: avoid \b with Arabic — JS word-boundaries don't treat Arabic letters as \w
    if (STEP_PATTERNS.sunscreen.test(text)) return { step: 'sunscreen', conf: 0.92 };
    // A "mist cream" is a moisturiser sold in a spray; only treat mist as a
    // toner-step product when the name doesn't also call it a cream.
    const mistCream = /ميست|\bmist\b/.test(text) && /كريم|\bcream\b/.test(text);
    if (!mistCream && STEP_PATTERNS.toner.test(text)) return { step: 'toner', conf: 0.92 };
    if (STEP_PATTERNS.cleanser.test(text)) return { step: 'cleanser', conf: 0.9 };
    if (STEP_PATTERNS.serum.test(text)) return { step: 'serum', conf: 0.9 };
    if (
      STEP_PATTERNS.moisturizer.test(text) &&
      !/hand cream|كريم اليد|كريم اليدين|lip|شفاه|eye cream|كريم العين|كريم العيون|سيرم العين|واقي|sun|sunscreen/.test(text)
    ) {
      return { step: 'moisturizer', conf: 0.86 };
    }
    return { step: null, conf: 0 };
  }

  /** Potent actives. `formulaText` may include the description — ingredient names are factual. */
  function inferActiveFamilies(formulaText) {
    return ACTIVE_LEXICON.filter((item) => item.re.test(formulaText)).map((item) => item.family);
  }

  /** Supporting ingredients present in the formula, by lexicon key. */
  function inferIngredients(formulaText) {
    return INGREDIENT_LEXICON.filter((item) => item.re.test(formulaText)).map((item) => item.key);
  }

  /** Texture / finish claims. Name scope only — see CLAIM_PATTERNS. */
  function inferClaims(nameText) {
    return Object.keys(CLAIM_PATTERNS).filter((key) => CLAIM_PATTERNS[key].test(nameText));
  }

  /**
   * Concerns the formula gives evidence for, with a strength count. Independent
   * of tags, so it can correct both missing and narrow tagging.
   *
   * An ingredient named in the product's own name counts double: descriptions
   * in this catalog list supporting ingredients generously, so body-copy
   * evidence must never outweigh what the product calls itself.
   */
  function inferFormulaConcerns(nameText, formulaText) {
    const strength = { pores: 0, acne: 0, pigmentation: 0, dryness: 0, wrinkles: 0 };
    function apply(lexicon, base) {
      lexicon.forEach((item) => {
        if (!item.re.test(formulaText)) return;
        const weight = item.re.test(nameText) ? base * 2 : base;
        item.concerns.forEach((concern) => {
          strength[concern] += weight;
        });
      });
    }
    apply(ACTIVE_LEXICON, 2);
    apply(INGREDIENT_LEXICON, 1);
    return strength;
  }

  function treatmentActiveFamilies(product, step) {
    if (!product || !product.meta || (step !== 'toner' && step !== 'serum' && step !== 'moisturizer')) return [];
    return (product.meta.nameActives || product.meta.activeFamilies || []).slice();
  }

  function activeIntensity(product, step) {
    const fams = treatmentActiveFamilies(product, step);
    const seen = {};
    let sum = 0;
    fams.forEach(function (family) {
      if (seen[family]) return;
      seen[family] = true;
      sum += ACTIVE_INTENSITY[family] || 0;
    });
    return sum;
  }

  function activeFamilySupportsConcern(family, concern) {
    for (let i = 0; i < ACTIVE_LEXICON.length; i++) {
      if (ACTIVE_LEXICON[i].family === family) {
        return ACTIVE_LEXICON[i].concerns.indexOf(concern) !== -1;
      }
    }
    return false;
  }

  /**
   * Hard duplicate-active only for strong irritants (intensity ≥ 2).
   * Mild leave-ons (niacinamide / TXA / Vit C / arbutin / glutathione / kojic)
   * are meant to layer with the serum in real brightening routines — blocking
   * them hid AXIS Glow / Celimax / Anua Peach / cherry cream behind generic gels.
   * Safety still comes from the leave-on intensity budget.
   */
  function isHarshDuplicateActive(family) {
    return (ACTIVE_INTENSITY[family] || 0) >= 2;
  }

  /**
   * Known harsh cross-family combinations that should never stack in one
   * routine even when neither family repeats and the total intensity budget
   * has room left (2026-08-07 audit fix — retinoid + BHA/salicylic/benzoyl
   * layered nightly is a classic over-exfoliation/irritation risk).
   */
  const HARSH_PAIR_BANS = [
    ['retinoid', 'exfoliating-acid'],
    ['retinoid', 'salicylic'],
    ['retinoid', 'benzoyl']
  ];
  function hasBannedHarshPair(activeFamilies, blockedActiveFamilies) {
    if (!activeFamilies || !activeFamilies.length || !blockedActiveFamilies || !blockedActiveFamilies.length) {
      return false;
    }
    return HARSH_PAIR_BANS.some(function (pair) {
      const a = pair[0];
      const b = pair[1];
      const usesA = activeFamilies.indexOf(a) !== -1;
      const usesB = activeFamilies.indexOf(b) !== -1;
      return (usesA && blockedActiveFamilies.indexOf(b) !== -1) || (usesB && blockedActiveFamilies.indexOf(a) !== -1);
    });
  }

  /**
   * Mild families (niacinamide, vitamin-c, TXA, arbutin, kojic, glutathione…)
   * are intentionally allowed to layer — blocking them on first repeat used
   * to hide good products (AXIS Glow / Anua Peach / Celimax, see the 2026-08-07
   * Polish notes). But "allowed to layer" was never meant to be unlimited:
   * 2026-08-07 (user-reported) a routine surfaced niacinamide in the toner,
   * serum, AND moisturizer simultaneously — three products doing the same
   * job reads as redundant, not curated. `blockedActiveFamilies` is now a
   * multiset (duplicates kept, not deduped on insert — see commitPick /
   * buildRoutineAroundAnchor / supportOptsFromRows), so a mild family caps
   * at 2 total occurrences (blocks the 3rd); harsh families still block on
   * the very first repeat, unchanged.
   *
   * `allowMildRepeat` (2026-08-07): toner-only escape hatch — see the
   * "1.5" retry pass in pickSupportFromQueue. When the mild-family cap has
   * already rejected every genuinely concern-relevant toner (e.g. every
   * pigmentation toner in stock happens to be niacinamide, and the serum +
   * moisturizer already used it twice), the fallback used to give up on
   * relevance entirely and grab whatever unrelated product was next in the
   * full catalog. This flag re-runs the SAME relevant tier with only the
   * mild-repeat ceiling lifted — harsh-family blocking and the banned-pair
   * check below are never affected by it.
   */
  function hasBlockedHarshActive(activeFamilies, blockedActiveFamilies, allowMildRepeat) {
    if (!activeFamilies || !activeFamilies.length || !blockedActiveFamilies || !blockedActiveFamilies.length) {
      return false;
    }
    const duplicateBlocked = activeFamilies.some(function (family) {
      const priorCount = blockedActiveFamilies.filter(function (f) {
        return f === family;
      }).length;
      if (priorCount === 0) return false;
      if (isHarshDuplicateActive(family)) return true;
      if (allowMildRepeat) return false;
      return priorCount >= 2;
    });
    return duplicateBlocked || hasBannedHarshPair(activeFamilies, blockedActiveFamilies);
  }

  function serumSupportsConcern(product, concern) {
    if (!product || !product.meta) return false;
    const meta = product.meta;
    if (meta.intelConcernEffects && meta.intelConcernEffects.length) {
      return meta.intelConcernEffects.some(function (e) {
        return e.quiz === concern && e.fit >= 0.55;
      });
    }
    if ((meta.concerns || []).indexOf(concern) !== -1) return true;
    if ((meta.activeFamilies || []).some((family) => activeFamilySupportsConcern(family, concern))) return true;

    const roles = meta.supportRoles || [];
    if (concern === 'dryness') {
      return roles.indexOf('hydrating') !== -1 || roles.indexOf('soothing') !== -1;
    }
    if (concern === 'pigmentation') return roles.indexOf('brightening') !== -1;
    if (concern === 'pores') return (meta.concerns || []).indexOf('acne') !== -1;
    if (concern === 'acne') return (meta.concerns || []).indexOf('pores') !== -1;
    return false;
  }

  /**
   * Support roles combine ingredient evidence (which may come from the
   * description) with name-scope claim language. The three original roles are
   * preserved so existing scoring rules keep working; barrier and
   * sebum-control are additions used by the new compatibility model.
   */
  function inferSupportRoles(nameText, formulaText) {
    const roles = [];
    function add(role) {
      if (role && roles.indexOf(role) === -1) roles.push(role);
    }

    INGREDIENT_LEXICON.forEach((item) => {
      if (item.re.test(formulaText)) item.roles.forEach(add);
    });

    // Claim language, name scope only.
    if (/soothing|calming|gentle|مهدئ|لطيف|تهدئه/.test(nameText)) add('soothing');
    if (/hydration|moist|ترطيب|جفاف|barrier|حاجز|aqua|أكوا|اكوا/.test(nameText)) add('hydrating');
    if (/barrier|حاجز|ترميم/.test(nameText)) add('barrier');
    if (CLAIM_PATTERNS.matte.test(nameText) || CLAIM_PATTERNS['oil-free'].test(nameText)) add('sebum-control');
    if (
      /bright|glow|dark.?spot|pigment|vitamin.?c|فيتامين\s*(?:سي|c)|تفتيح|توهج|نضارة|بقع|تصبغ|اشراق|إشراق|خوخ|peach|أرز|ارز|rice/.test(
        nameText
      )
    ) {
      add('brightening');
    }
    return roles;
  }

  function inferMeta(product, opts) {
    opts = opts || {};
    const tags = product.tags || [];
    const title = product.title || '';
    const handle = product.handle || '';
    const tagText = tags.join(' ');
    const titleNorm = normalize([title, handle].join(' '));
    // Name scope: what the product calls itself. Trustworthy for step/texture.
    const nameText = normalize([title, handle, tagText].join(' '));
    // Formula scope: adds the description, which is reliable for ingredient
    // names but not for skin-type or texture claims.
    // Skipped when sondos_intel already supplies clinical fields (Phase B).
    const bodyText = opts.skipFormula
      ? ''
      : product.bodyText || (product.bodyHtml ? plainText(product.bodyHtml) : '');
    const formulaText = bodyText ? nameText + ' ' + normalize(bodyText) : nameText;

    const skins = new Set();
    const concerns = new Set();
    const tagSkins = new Set();
    const tagConcerns = new Set();
    let texture = null;
    let strength = null;
    let excluded = isNonRoutineProduct(product);
    let priority = Number(product.aiPriority || 0) || 0;
    let fromLegacyTags = false;
    const titleConcerns = inferConcernsFromTitle(title, handle);

    tags.forEach((raw) => {
      const tag = normalize(raw);
      if (tag === 'ai-exclude') excluded = true;
      if (tag === 'skin-oily') skins.add('oily');
      if (tag === 'skin-dry') skins.add('dry');
      if (tag === 'skin-combination') skins.add('combination');
      if (tag === 'skin-sensitive') skins.add('sensitive');
      if (tag === 'skin-normal') skins.add('normal');
      if (tag === 'concern-pores') concerns.add('pores');
      if (tag === 'concern-pigmentation') concerns.add('pigmentation');
      if (tag === 'concern-acne') concerns.add('acne');
      if (tag === 'concern-dryness') concerns.add('dryness');
      if (tag === 'concern-wrinkles' || tag === 'concern-aging') concerns.add('wrinkles');
      if (tag === 'texture-gel') texture = 'gel';
      if (tag === 'texture-cream') texture = 'cream';
      if (tag === 'texture-watery') texture = 'watery';
      if (tag === 'strength-gentle') strength = 'gentle';
      if (tag === 'strength-active') strength = 'active';

      const legacy = parseLegacyTag(raw);
      if (legacy.skins.length || legacy.concerns.length) fromLegacyTags = true;
      legacy.skins.forEach((sk) => skins.add(sk));
      legacy.concerns.forEach((c) => concerns.add(c));
    });

    // Snapshot what the tags alone claimed, before inference widens the sets.
    // Compatibility scoring needs to know which claims are asserted versus guessed.
    skins.forEach((sk) => tagSkins.add(sk));
    concerns.forEach((c) => tagConcerns.add(c));

    // Title concerns always merge (tags alone are incomplete / sometimes wrong)
    titleConcerns.forEach((c) => concerns.add(c));

    // Title/skin signals also merge so oily products aren't stuck on a legacy combo tag
    if (/دهني|\boily\b|oil control|سيبم|زهم|sebum|لادا|oil.?free|غير دهني/.test(titleNorm + ' ' + normalize(tagText))) {
      skins.add('oily');
    }
    // Pore / blemish / volcanic-clay products → typically oily–combination
    // (do not use bare جيجو — orchid "جزيرة جيجو" is not pore care)
    if (/ازيليك|azelaic|blemish|عيوب|حبوب|acne|مسام|pore|طين بركان|بركان|volcanic|volcan|جيجو فولكانيك|غسول الطين/.test(titleNorm)) {
      skins.add('oily');
      skins.add('combination');
    }
    if (/جاف|dry skin|dehydrated|للجافة|بشرة جافة/.test(titleNorm)) skins.add('dry');
    if (/مختلط|combination/.test(titleNorm + ' ' + normalize(tagText))) skins.add('combination');
    if (/حساس|sensitive|redness|احمرار/.test(titleNorm)) skins.add('sensitive');
    if (/جميع انواع|كل انواع|all skin|مناسب لجميع|لكل انواع/.test(titleNorm)) skins.add('normal');

    if (!texture) {
      if (/gel\b|جل\b|jelly|watery|aqua|lightweight|خفيف|emulsion|لوشن|gel cream|water cream/.test(titleNorm)) {
        texture = 'gel';
      } else if (/cream|كريم|غني|rich|balm|زبدة/.test(titleNorm)) {
        texture = 'cream';
      } else if (/toner|تونر|essence|mist|مائي/.test(titleNorm)) {
        texture = 'watery';
      }
    }

    if (!strength) {
      if (/retinol|a\.?h\.?a|b\.?h\.?a|salicylic|peel|acid 10|retin|باكوتشيول|bakuchiol/.test(titleNorm)) {
        strength = 'active';
      } else if (/centella|heartleaf|panthenol|ceramide|hyaluron|هيالورون|soothing|مهدئ|لطيف|سينتيلا|هارتليف/.test(titleNorm)) {
        strength = 'gentle';
      }
    }

    const activeFamilies = inferActiveFamilies(formulaText);
    // Name-scope actives describe what the product IS. Layering safety and the
    // irritation budget must use these: a hydrating cream whose copy mentions
    // niacinamide is not an active treatment and must not be gated like one.
    const nameActives = inferActiveFamilies(nameText);
    const ingredients = inferIngredients(formulaText);
    const claims = inferClaims(nameText);
    const formulaConcerns = inferFormulaConcerns(nameText, formulaText);
    const stepInfo = inferStep(product);
    const brand = productBrand(product);
    const universal = skins.has('normal') || /جميع انواع|كل انواع|all skin|مناسب لجميع/.test(titleNorm);
    const fromCollections = Array.isArray(product.collections) ? product.collections.map(normalizeHandle).filter(Boolean) : [];
    // Collection membership stays supporting evidence; it must not create a clinical concern tag.
    const concernCollections = [...new Set(
      fromCollections
        .map(concernFromCollectionHandle)
        .filter(Boolean)
    )];

    const tagged =
      fromLegacyTags || (product.tags || []).some((t) => /^skin-|^concern-|^step-/.test(normalize(t)));

    /**
     * How much the engine actually knows about this product. Used to discount
     * scores rather than to filter: a product we know little about should not
     * beat a well-evidenced match, but it must still be reachable.
     */
    const evidenceSources =
      (tagSkins.size || tagConcerns.size ? 1 : 0) +
      (concernCollections.length ? 1 : 0) +
      (titleConcerns.length ? 1 : 0) +
      (activeFamilies.length || ingredients.length ? 1 : 0);
    const confidence = Math.min(1, 0.35 + 0.16 * evidenceSources + (bodyText ? 0.1 : 0));

    return {
      step: stepInfo.step,
      stepConf: stepInfo.conf,
      skins: [...skins],
      concerns: [...concerns],
      tagSkins: [...tagSkins],
      tagConcerns: [...tagConcerns],
      titleConcerns: titleConcerns,
      texture: texture,
      strength: strength,
      excluded: excluded,
      priority: priority,
      universal: universal,
      brand: brand,
      activeFamilies: activeFamilies,
      nameActives: nameActives,
      ingredients: ingredients,
      claims: claims,
      formulaConcerns: formulaConcerns,
      supportRoles: inferSupportRoles(nameText, formulaText),
      hasDescription: !!bodyText,
      confidence: confidence,
      tagged: tagged,
      sourceCollections: fromCollections,
      concernCollections: concernCollections
    };
  }

  function enrich(product) {
    const intel = lookupIntel(product);
    // When clinical intel exists, skip heavy body_html formula scans (plan Phase B).
    const hasClinicalIntel = !!(
      intel &&
      (intel.step ||
        (intel.skins && intel.skins.length) ||
        (intel.concernEffects && intel.concernEffects.length))
    );
    const meta = inferMeta(product, { skipFormula: hasClinicalIntel });
    if (intel) applyIntelToMeta(meta, intel, product);
    return Object.assign({}, product, { meta: meta, intel: intel || null });
  }

  function scoreProduct(product, step, skin, concern, usedBrands, opts) {
    opts = opts || {};
    const m = product.meta;
    if (!isHardEligible(product, step, skin, concern)) {
      return { score: -Infinity, reasons: ['skip'] };
    }

    const activeFamilies = treatmentActiveFamilies(product, step);
    const blockedActiveFamilies = opts.blockedActiveFamilies || [];
    // Toner exemption (see hasBlockedHarshActive's allowMildRepeat note): this is
    // the EARLY gate scoreProduct itself applies, upstream of pickSupportFromQueue's
    // tier/band logic — without it excluded here, a candidate never reaches the
    // ranked list at all, so the later supportPickPasses relaxation never gets a
    // chance to matter.
    if (hasBlockedHarshActive(activeFamilies, blockedActiveFamilies, step === 'toner')) {
      return { score: -Infinity, reasons: ['duplicate-active'] };
    }
    // Leave-on budget (cleanser already excluded from treatmentActiveFamilies).
    // Sensitive stack=2 after an anchor is locked; a solo serum anchor may be 3
    // (e.g. azelaic+kojic+tea-tree) so Purito/Anua paths aren't wiped before pick.
    let maxIntensity = skin === 'sensitive' ? 2 : skin === 'dry' ? 3 : 4;
    if (skin === 'sensitive' && step === 'serum' && !(opts.activeIntensity > 0)) {
      maxIntensity = 3;
    }
    // Intensity-0 support (gels, ceramides) must stay eligible even if the serum
    // alone already filled the sensitive budget (Purito azelaic stack = 3).
    const addIntensity = activeIntensity(product, step);
    if (addIntensity > 0 && (opts.activeIntensity || 0) + addIntensity > maxIntensity) {
      return { score: -Infinity, reasons: ['active-overload'] };
    }
    // Toner + serum: prefer prep/soothe over a second harsh treatment.
    // Brightening leave-ons (Vit C / arbutin / glutathione / TXA) that MATCH the
    // quiz concern must NOT get the old −72 dump — that hid Anua Peach / AXIS Glow.
    let tonerActiveSoftPenalty = 0;
    if (step === 'toner' && opts.supportiveOnly) {
      const tonerIntensity = activeIntensity(product, step);
      const hasUnrelatedActive =
        tonerIntensity > 0 &&
        activeFamilies.some((family) => !activeFamilySupportsConcern(family, concern));
      const hasHarsh =
        activeFamilies.indexOf('exfoliating-acid') !== -1 ||
        activeFamilies.indexOf('salicylic') !== -1 ||
        activeFamilies.indexOf('retinoid') !== -1 ||
        activeFamilies.indexOf('benzoyl') !== -1;
      if (hasUnrelatedActive || hasHarsh) {
        tonerActiveSoftPenalty = hasHarsh ? 55 : 72;
      } else if (tonerIntensity > 1) {
        tonerActiveSoftPenalty = 14;
      } else if (tonerIntensity === 1) {
        tonerActiveSoftPenalty = 8;
      }
    }
    // 2026-08-07: this used to hard-reject (-Infinity) a support moisturizer
    // whenever it had ANY concern-unrelated active, or simply had more than
    // one tracked active regardless of relevance — both conditions, either
    // alone, disqualified the product outright. That was harsher than the
    // near-identical toner block above, which treats the same two situations
    // (unrelated active / multiple actives) as graduated SOFT penalties, not
    // a veto. The asymmetry meant a moisturizer with two fully concern-relevant
    // actives (e.g. niacinamide + tea-tree, both acne-supporting) lost outright
    // to a product with zero tracked actives at all (fit from generic tagging
    // only) — worse evidence winning purely because it had nothing to penalize.
    // Now scored like toner: unrelated-active is a heavy soft penalty (still
    // usually loses to a clean match, but isn't an automatic disqualification),
    // and "more than one relevant active" costs less.
    let moisturizerActiveSoftPenalty = 0;
    if (step === 'moisturizer' && opts.supportiveMoisturizer) {
      const moisturizerIntensity = activeIntensity(product, step);
      const hasUnrelatedActive =
        moisturizerIntensity > 0 &&
        activeFamilies.some((family) => !activeFamilySupportsConcern(family, concern));
      if (hasUnrelatedActive) {
        moisturizerActiveSoftPenalty = 72;
      } else if (moisturizerIntensity > 1) {
        moisturizerActiveSoftPenalty = 14;
      }
    }
    if (step === 'moisturizer' && opts.supportiveMoisturizer) {
      const moisturizerRoles = m.supportRoles || [];
      const brighteningOnly =
        moisturizerRoles.indexOf('brightening') !== -1 &&
        moisturizerRoles.indexOf('soothing') === -1 &&
        moisturizerRoles.indexOf('hydrating') === -1;
      const hasConcernActive = activeFamilies.some((family) => activeFamilySupportsConcern(family, concern));
      if (brighteningOnly && concern !== 'pigmentation' && !hasConcernActive && !m.concerns.includes(concern)) {
        return { score: -Infinity, reasons: ['moisturizer-treatment-overlap'] };
      }
    }

    let score = 10 * (m.stepConf || 0.7);
    const reasons = [];
    // Soft penalties for negative safety flags (Unknown after desc soften = no hit).
    // Prefer rank-down over hard catalog delete (locked decision #5).
    if (m.acneSafe === false && (concern === 'acne' || concern === 'pores' || skin === 'oily')) {
      score -= 28;
      reasons.push('acne-safe-no');
    }
    if (m.sensitiveSafe === false && skin === 'sensitive') {
      score -= 36;
      reasons.push('sensitive-safe-no');
    }
    if (m.strength === 'active' && skin === 'sensitive') {
      score -= 48;
      reasons.push('active-on-sensitive');
    }
    if (
      (skin === 'sensitive' || skin === 'dry') &&
      (step === 'serum' || step === 'toner') &&
      (m.nameActives || m.activeFamilies || []).some(function (family) {
        return (
          family === 'exfoliating-acid' ||
          family === 'salicylic' ||
          family === 'retinoid' ||
          family === 'benzoyl'
        );
      })
    ) {
      score -= skin === 'sensitive' ? 40 : 22;
      reasons.push('harsh-on-' + skin);
    }
    const concernHit = m.concerns.indexOf(concern) !== -1;
    const serumSupportHit = step === 'serum' && serumSupportsConcern(product, concern);
    const related = (RELATED_CONCERNS[concern] || []).some((c) => m.concerns.indexOf(c) !== -1);
    const skinHit = m.skins.indexOf(skin) !== -1;
    const concernMult = CONCERN_STEP_MULT[step] || 1;
    const concernCollectionHit = (m.concernCollections || []).indexOf(concern) !== -1;
    const preferredConcernHandles = resolveConcernCollectionHandles(concern).map(handleKey);
    const sourceHandleKeys = (m.sourceCollections || []).map(handleKey);
    const exactConcernHandleHit = preferredConcernHandles.some((h) => sourceHandleKeys.indexOf(h) !== -1);

    // --- Concern relevance -------------------------------------------------
    // One graded signal for every step, replacing five hand-tuned branches
    // where a single tag was worth +90 and its absence -90.
    const evidence = concernEvidence(product, concern);
    if (serumSupportHit && evidence.fit < 0.5) {
      evidence.fit = 0.5;
      evidence.sources.push('serum-support');
    }
    score += getConcernFitWeight(step, concern) * concernMult * evidence.fit;
    if (evidence.sources.length) reasons.push('concern:' + evidence.sources.join('+'));

    // A treatment step that treats nothing is a real miss, not a mild one.
    if ((step === 'serum' || step === 'toner') && evidence.fit < TREATMENT_FIT_FLOOR) {
      score -= step === 'serum' ? 55 : 30;
      reasons.push('no-concern');
    }

    // The anchor should be self-evidently right. A serum whose own name states
    // the concern is easier for a customer to trust than one that merely
    // contains a relevant ingredient, so name-level clarity earns extra weight
    // at this step only.
    if (step === 'serum' && (m.titleConcerns || []).indexOf(concern) !== -1) {
      score += 22;
      reasons.push('named-treatment');
    }
    // Pigmentation anchors should be brightening paths — not retinol serums that
    // merely mention TXA in copy (e.g. Purito climbing into oily×pigmentation top 5).
    if (
      step === 'serum' &&
      concern === 'pigmentation' &&
      activeFamilies.indexOf('retinoid') !== -1
    ) {
      score -= 60;
      reasons.push('retinoid-secondary-for-pigment');
    }

    if (step === 'toner') {
      const supportRoles = m.supportRoles || [];
      const soothing = supportRoles.indexOf('soothing') !== -1;
      const hydrating = supportRoles.indexOf('hydrating') !== -1;
      const brighteningOnly =
        supportRoles.indexOf('brightening') !== -1 && !soothing && !hydrating;
      if (opts.supportiveOnly) {
        // The serum already carries the treatment; the toner should prep and calm.
        if (soothing) score += 26;
        if (hydrating) score += 24;
        if (brighteningOnly) score += concern === 'pigmentation' ? 28 : 4;
        if (m.texture === 'watery' || m.texture === 'gel') score += 12;
        reasons.push('supportive-toner');
        if (tonerActiveSoftPenalty) {
          score -= tonerActiveSoftPenalty;
          reasons.push('toner-active-soft');
        }
      } else if (m.strength === 'gentle' || m.texture === 'watery' || m.texture === 'gel') {
        score += 12;
      }
    } else if (step === 'cleanser' || step === 'moisturizer') {
      if (step === 'moisturizer' && opts.supportiveMoisturizer) {
        const supportRoles = m.supportRoles || [];
        if (supportRoles.indexOf('soothing') !== -1) score += 10;
        if (supportRoles.indexOf('hydrating') !== -1) score += 12;
        if (moisturizerActiveSoftPenalty) {
          score -= moisturizerActiveSoftPenalty;
          reasons.push('moisturizer-active-soft');
        }
        if (
          skin === 'oily' &&
          /oil.?free|non.?comedogenic|lightweight|gel|جل|lotion|لوشن|snail|حلزون|green.?tea|شاي.?اخضر/.test(
            normalize([product.title, product.handle].join(' '))
          )
        ) {
          score += 16;
          reasons.push('lightweight-moisturizer');
        }
      }
    } else if (step === 'sunscreen') {
      score += 24;
      if (skin === 'oily' && concern === 'pores') {
        const oilyPoreTitleFit =
          /oil.?free|non.?comedogenic|lightweight|matte|oil.?control|sebum|خفيف|غير دهني|تحكم.*زيوت|مسام|pore/.test(
            normalize(product.title || '')
          );
        if (m.skins.indexOf('oily') !== -1) {
          score += 10;
          reasons.push('oily-sunscreen');
        }
        if (m.concerns.indexOf('pores') !== -1) {
          score += 10;
          reasons.push('pore-sunscreen');
        }
        if (oilyPoreTitleFit) {
          score += 8;
          reasons.push('light-oily-finish');
        }
      }
    }
    if (concernCollectionHit) {
      if (step === 'serum' || step === 'toner') score += 8;
      else if (step === 'cleanser' || step === 'moisturizer') score += 3;
      else score += 2;
      reasons.push('concern-collection');
    }
    if (exactConcernHandleHit) {
      if (step === 'serum' || step === 'toner') score += 6;
      else if (step === 'cleanser' || step === 'moisturizer') score += 2;
      else score += 2;
      reasons.push('concern-collection-exact');
    }

    // Pores vs acne: mild penalty only (don't erase half the moisturizer shelf)
    if (concern === 'pores' && (step === 'moisturizer' || step === 'serum' || step === 'toner')) {
      const titleCheck = normalize(product.title || '');
      const isPoreNamed = PORE_TITLE_RE.test(titleCheck);
      const isAcneNamed = /ازيليك|azelaic|blemish|عيوب|اثار|آثار|scar|حبوب|acne/.test(titleCheck);
      if (isAcneNamed && !isPoreNamed) {
        score -= step === 'moisturizer' ? 28 : 45;
        reasons.push('acne-not-pores');
      }
      if (isPoreNamed && step === 'moisturizer') {
        score += 12;
        reasons.push('pore-moisturizer');
      }
    }
    if (concern === 'acne' && step === 'moisturizer') {
      const titleCheck = normalize(product.title || '');
      if (/ازيليك|azelaic|blemish|عيوب|حبوب|acne/.test(titleCheck)) {
        score += 16;
        reasons.push('acne-moisturizer');
      }
    }

    // --- Skin fit ----------------------------------------------------------
    // A single graded term: confirmed match > unlabelled > opposite label.
    const isBase = step === 'cleanser' || step === 'moisturizer' || step === 'sunscreen';
    const affinity = skinAffinity(product, skin, step, concern);
    const skinWeight = SKIN_AFFINITY_WEIGHT[step] || 36;
    score += skinWeight * affinity * (affinity < 0 ? SKIN_MISMATCH_MULTIPLIER : 1);
    if (skinHit) score += SKIN_EXACT_BONUS;
    if (skinHit) reasons.push('skin');
    else if (m.universal) reasons.push('skin-universal');
    else if (!m.skins.length) reasons.push('skin-unknown');
    else if (affinity < 0) reasons.push('skin-mismatch');
    else reasons.push('skin-neighbor');

    // Dry skin asking about dryness is well served by a dedicated dry-skin cream.
    if (skin === 'normal' && concern === 'dryness' && step === 'moisturizer' && m.skins.indexOf('dry') !== -1) {
      score += 12;
      reasons.push('skin-dryness-neighbor');
    }

    // Texture fit — softer cream penalty so good creams still rotate for oily
    const texPref = (SKIN_TEXTURE_PREF[skin] || {})[m.texture || ''] || 0;
    score += isBase && texPref < 0 ? Math.round(texPref * 0.55) : texPref;

    if (skin === 'sensitive') {
      if (m.strength === 'active') score -= 55;
      else if (m.strength === 'gentle') score += 24;
    }
    if ((skin === 'dry' || concern === 'dryness') && m.strength === 'active' && (step === 'cleanser' || step === 'toner')) {
      score -= 30;
    }
    if ((skin === 'oily' || concern === 'acne' || concern === 'pores') && m.texture === 'cream' && step === 'moisturizer') {
      score -= concernHit || related || exactConcernHandleHit ? 4 : 14;
    }
    if ((skin === 'oily' || concern === 'acne' || concern === 'pores') && (m.texture === 'gel' || m.texture === 'watery')) {
      score += 10;
    }
    if ((skin === 'dry' || concern === 'dryness') && m.texture === 'cream') score += 14;

    // Name-level concern signals are already counted by concernEvidence, so
    // only the corrective nudges remain here.
    const titleN = normalize(product.title || '');
    if (step === 'cleanser' && concern !== 'pores' && concern !== 'acne' && PORE_TITLE_RE.test(titleN)) {
      score -= 20;
      reasons.push('cleanser-wrong-focus');
    }
    if (
      skin === 'oily' &&
      /دهني|oil.?control|sebum|رغوي|foam|gel cleanser|غسول.*مسام|salicylic|ساليسيليك|طين بركان|بركان|volcanic|غسول الطين|clay/.test(
        titleN
      )
    ) {
      score += isBase ? 8 : 14;
    }

    // Volcanic/clay cleansers are occasional deep-cleansing options, not the default daily anchor.
    if (
      step === 'cleanser' &&
      /طين بركان|غسول الطين البركان|volcanic.?clay|clay cleanser|بركاني/.test(titleN)
    ) {
      score -= skin === 'oily' && concern === 'pores' ? 6 : 30;
      reasons.push('volcanic-clay-occasional');
    }

    // Prefer products that already matched the chosen serum's concern family
    if (opts.preferConcern && concernHit) score += 18;

    // Brand coherence is a tie-breaker only; it never narrows the pool.
    const productBrand = brandFamily(product);
    if (opts.preferBrand && productBrand === opts.preferBrand) {
      score += 6;
      reasons.push('brand-lock');
    } else if (Array.isArray(opts.brandChain)) {
      const idx = opts.brandChain.indexOf(productBrand);
      if (idx === 0) {
        score += 6;
        reasons.push('brand-chain-1');
      } else if (idx === 1) {
        score += 4;
        reasons.push('brand-chain-2');
      } else if (idx > 1) {
        score += 2;
        reasons.push('brand-chain-n');
      }
    }

    // How much we actually know about this product. Replaces a flat +10 for
    // "has any structured tag": a product with agreeing tags, collection and
    // formula evidence is more trustworthy than one with a single stray tag.
    score += 20 * ((m.confidence || 0.5) - 0.5);
    reasons.push('confidence:' + Math.round((m.confidence || 0.5) * 100) + '%');

    score += Math.max(0, Math.min(15, m.priority || 0));

    const brand = productBrand;
    if (brand && usedBrands[brand]) {
      score -= 8 * usedBrands[brand];
    }

    // Tie-break on corroboration rather than title length: when two products
    // score alike, prefer the one whose suitability is evidenced from more
    // independent directions.
    score += 0.6 * evidence.sources.length + 0.3 * Math.min(4, (m.ingredients || []).length);

    return {
      score: Math.round(score * 10) / 10,
      reasons: reasons,
      concernHit: concernHit,
      skinHit: skinHit,
      fit: evidence.fit,
      affinity: affinity,
      sources: evidence.sources
    };
  }

  function routineStorageKey(skin, concern) {
    return 'arb-last-routine:' + skin + ':' + concern;
  }

  function getRecentIds(skin, concern) {
    try {
      const raw = sessionStorage.getItem(routineStorageKey(skin, concern));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function rememberRoutine(skin, concern, routine) {
    try {
      const ids = routine.map((row) => String(row.product.id));
      // Keep enough history to avoid repeating SPF across roughly 12 rerolls
      const prev = getRecentIds(skin, concern);
      const merged = ids.concat(prev).slice(0, 60);
      sessionStorage.setItem(routineStorageKey(skin, concern), JSON.stringify(merged));
    } catch (e) {}
  }

  function rankCandidates(pool, step, skin, concern, usedIds, usedBrands, opts, recentIds) {
    const ranked = [];
    pool.forEach((p) => {
      if (usedIds.has(p.id)) return;
      const result = scoreProduct(p, step, skin, concern, usedBrands, opts);
      if (!isFinite(result.score) || result.score <= -80) return;
      let score = result.score;
      // A nudge away from recently shown products, sized to break ties between
      // comparable options. Rotation is decided at routine level; a penalty
      // large enough to override quality here is what made rerolls worse.
      if (recentIds.indexOf(String(p.id)) !== -1) score -= 20;
      // Jitter widens the candidate set the routine-level search explores.
      score += (Math.random() * 6) - 3;
      ranked.push({
        product: p,
        score: score,
        baseScore: result.score,
        meta: result
      });
    });
    ranked.sort((a, b) => b.score - a.score);
    return ranked;
  }

  /** Rotate only inside a quality band; broader bands are reserved for safe support steps. */
  function pickDiverse(pool, step, skin, concern, usedIds, usedBrands, opts, recentIds) {
    recentIds = recentIds || [];
    let ranked = rankCandidates(pool, step, skin, concern, usedIds, usedBrands, opts, recentIds);
    if (!ranked.length) return { product: null, score: -Infinity, meta: null };

    const byBase = ranked.slice().sort((a, b) => b.baseScore - a.baseScore);
    const topBaseScore = byBase[0].baseScore;
    // One rule instead of nine hand-tuned special cases: rotate more freely
    // where the choice matters less. The treatment steps carry the concern and
    // stay tight; base steps are more interchangeable, so a wider band there
    // buys variety cheaply. Routine-level scoring is what now guards against a
    // wide band producing an incoherent combination.
    const isTreatmentStep = step === 'serum' || step === 'toner';
    const margin = isTreatmentStep ? 32 : 55;
    const maxCandidates = isTreatmentStep ? 5 : 7;
    let topEligible = byBase
      .filter((r) => r.baseScore >= topBaseScore - margin)
      .slice(0, maxCandidates);
    const minimumRotationSize = Math.min(5, maxCandidates, byBase.length);
    if (topEligible.length < minimumRotationSize) {
      topEligible = byBase.slice(0, minimumRotationSize);
    }
    const fresh = topEligible.filter((r) => recentIds.indexOf(String(r.product.id)) === -1);
    let shortlist = fresh.length ? fresh : topEligible;
    if (!fresh.length && topEligible.length > 1) {
      const oldestIndex = Math.max(
        ...topEligible.map((candidate) => recentIds.indexOf(String(candidate.product.id)))
      );
      shortlist = topEligible.filter(
        (candidate) => recentIds.indexOf(String(candidate.product.id)) === oldestIndex
      );
    }
    const topScore = shortlist[0].baseScore;
    const temp = 8;
    const weights = shortlist.map((r) => Math.exp((r.baseScore - topScore) / temp));
    let total = 0;
    for (let i = 0; i < weights.length; i++) total += weights[i];
    let roll = Math.random() * total;
    let chosen = shortlist[0];
    for (let i = 0; i < shortlist.length; i++) {
      roll -= weights[i];
      if (roll <= 0) {
        chosen = shortlist[i];
        break;
      }
    }

    return {
      product: chosen.product,
      score: chosen.baseScore,
      meta: chosen.meta,
      selectionRank: topEligible.indexOf(chosen) + 1
    };
  }

  function pickBest(pool, step, skin, concern, usedIds, usedBrands, opts) {
    return pickDiverse(pool, step, skin, concern, usedIds, usedBrands, opts, []);
  }

  function commitPick(picked, usedIds, usedBrands, usedActiveFamilies, activeState, step, pick, floor) {
    if (!pick || !pick.product || !(pick.score >= floor)) return false;
    picked[step] = pick;
    usedIds.add(pick.product.id);
    const b = brandFamily(pick.product);
    if (b) usedBrands[b] = (usedBrands[b] || 0) + 1;
    // Kept as a multiset (duplicates allowed) so hasBlockedHarshActive can
    // count occurrences, not just detect presence — see its 2026-08-07 note.
    treatmentActiveFamilies(pick.product, step).forEach((family) => {
      usedActiveFamilies.push(family);
    });
    activeState.intensity += activeIntensity(pick.product, step);
    return true;
  }

  /** Per-product fitness (separate from routineFit — plan decision #15). */
  function productFit(product, step, skin, concern, usedBrands, opts) {
    return scoreProduct(product, step, skin, concern, usedBrands || {}, opts || {});
  }

  /** Routine-level fitness; may reject even when every productFit is strong. */
  function routineFit(rows, skin, concern) {
    const quality = scoreRoutine(rows, skin, concern);
    const reject =
      !rows ||
      rows.length < STEPS.length ||
      !isFinite(quality.score) ||
      quality.score < -40 ||
      (quality.notes || []).indexOf('weak-anchor') !== -1 ||
      (quality.notes || []).indexOf('missing:' + (STEPS.length - (rows ? rows.length : 0))) !== -1;
    return {
      score: quality.score,
      notes: quality.notes || [],
      intensity: quality.intensity,
      onConcern: quality.onConcern,
      reject: !!reject
    };
  }

  function whyAnchor(product, concern) {
    if (!product) return '';
    const fit = concernEvidence(product, concern).fit;
    const intelActives =
      (product.meta && product.meta.intel && product.meta.intel.actives) ||
      (product.intel && product.intel.actives) ||
      [];
    const shown = intelActives.slice(0, 2);
    const activeBit = shown.length ? ' ويحتوي ' + shown.join(' و ') : '';
    const goal = CONCERN_WHY_AR[concern] || 'بشرتك';
    if (fit >= 0.55) return 'اخترناه لأنه يساعد في ' + goal + activeBit + '.';
    return 'اخترناه كمرساة روتين مناسبة لبشرتك' + activeBit + '.';
  }

  /**
   * Short per-product tag line shown under each result card — no LLM,
   * template + meta only. Step-specific phrasing (2026-08-07 fix): the
   * first version used the identical "يدعم علاج <concern>" phrase for every
   * concern-relevant step, so a 5-product routine repeated the same purpose
   * line five times and lost its value. Each step now names its own role
   * (cleanse / prep / treat / hydrate / protect), with concern support
   * folded in only where genuinely true for that step.
   */
  const CONCERN_LABEL_AR = {
    pores: 'المسام',
    pigmentation: 'التصبغات',
    acne: 'الحبوب',
    dryness: 'الترطيب والجفاف',
    wrinkles: 'الخطوط الدقيقة'
  };
  const TEXTURE_TAG_AR = { watery: 'قوام مائي خفيف', gel: 'قوام جل', cream: 'قوام كريمي', foam: 'قوام رغوي' };
  const SKIN_LABEL_AR = { oily: 'الدهنية', dry: 'الجافة', combination: 'المختلطة', sensitive: 'الحساسة', normal: 'العادية' };

  function stepPurposeLine(step, hasFit, concernLabel) {
    switch (step) {
      case 'cleanser':
        return hasFit ? 'تنظيف عميق يدعم مظهر ' + concernLabel : 'تنظيف يومي لطيف';
      case 'toner':
        return hasFit ? 'يدعم علاج ' + concernLabel : 'يهيئ البشرة للخطوات التالية';
      case 'serum':
        return hasFit ? 'العلاج الأساسي لمظهر ' + concernLabel : 'يدعم توازن بشرتك';
      case 'moisturizer':
        return hasFit ? 'ترطيب يدعم علاج ' + concernLabel : 'ترطيب وتهدئة يومية';
      case 'sunscreen':
        return hasFit ? 'حماية يومية تدعم علاج ' + concernLabel : 'حماية يومية من الشمس';
      default:
        return hasFit ? 'يدعم علاج ' + concernLabel : 'يدعم توازن بشرتك';
    }
  }

  function productTagLine(product, step, skin, concern) {
    if (!product || !product.meta) return '';
    const m = product.meta;
    const parts = [];
    const fit = concern ? concernEvidence(product, concern).fit : 0;
    const concernLabel = CONCERN_LABEL_AR[concern] || '';
    parts.push(stepPurposeLine(step, fit > 0 && !!concernLabel, concernLabel));

    // 2026-08-07: sondos_intel.texture now carries 'foam' natively (the field
    // is free-text, not a real Shopify enum — the old Foam→Gel coercion in the
    // sync scripts was an unnecessary self-imposed restriction that made a
    // foaming cleanser's card visibly contradict its own title). Prefer the
    // synced metafield; the title regex is only a fallback for a product that
    // hasn't been (re-)synced yet.
    if (m.texture && TEXTURE_TAG_AR[m.texture]) {
      parts.push(TEXTURE_TAG_AR[m.texture]);
    } else if (step === 'cleanser' && /رغو|foam/i.test(normalize(product.title || ''))) {
      parts.push('قوام رغوي');
    }

    if (m.strength === 'gentle') {
      parts.push('لطيف');
    } else if (skin && (m.skins || []).indexOf(skin) !== -1 && SKIN_LABEL_AR[skin]) {
      parts.push('مناسب للبشرة ' + SKIN_LABEL_AR[skin]);
    }
    return parts.join(' · ');
  }

  /**
   * 2026-08-07 (user-reported): slice(0, 2) after alphabetical sort silently
   * dropped a 3rd/4th tracked family, so two genuinely different serums that
   * happened to share their first two (alphabetically) families collapsed to
   * the identical path string — e.g. "Anua Peach" (arbutin, niacinamide) and
   * "Anua Niacinamide+TXA" (arbutin, niacinamide, TRANEXAMIC) both became
   * "arbutin+niacinamide", losing exactly the ingredient (TXA) that made the
   * second one distinct. MMR then treated a tied-top-score serum as a
   * duplicate of a weaker one and dropped it, while a genuinely weaker
   * anchor (lower raw score, but a truly unique path) filled the slot
   * instead. Use the full tracked-family set, not just the first two.
   */
  function clinicalPath(rows) {
    const serum = (rows || []).find((row) => row && row.step === 'serum' && row.product);
    if (!serum) return 'none';
    const fams = (serum.product.meta.nameActives || serum.product.meta.activeFamilies || [])
      .filter((f) => CLINICAL_FAMILIES.indexOf(f) !== -1)
      .slice()
      .sort();
    if (fams.length) return fams.join('+');
    return 'brand:' + (brandFamily(serum.product) || serum.product.id);
  }

  function routineStepDiff(a, b) {
    let diff = 0;
    STEPS.forEach((step) => {
      const ra = (a || []).find((r) => r.step === step);
      const rb = (b || []).find((r) => r.step === step);
      const ida = ra && ra.product ? ra.product.id : null;
      const idb = rb && rb.product ? rb.product.id : null;
      if (ida !== idb) diff += 1;
    });
    return diff;
  }

  function familyJaccard(rowsA, rowsB) {
    const fa = {};
    const fb = {};
    (rowsA || []).forEach((r) => {
      (r.product.meta.nameActives || r.product.meta.activeFamilies || []).forEach((f) => {
        fa[f] = true;
      });
    });
    (rowsB || []).forEach((r) => {
      (r.product.meta.nameActives || r.product.meta.activeFamilies || []).forEach((f) => {
        fb[f] = true;
      });
    });
    const keysA = Object.keys(fa);
    const keysB = Object.keys(fb);
    if (!keysA.length && !keysB.length) return 0;
    let inter = 0;
    keysA.forEach((k) => {
      if (fb[k]) inter += 1;
    });
    const union = {};
    keysA.forEach((k) => {
      union[k] = true;
    });
    keysB.forEach((k) => {
      union[k] = true;
    });
    return inter / (Object.keys(union).length || 1);
  }

  function hasBarrierRole(product) {
    const roles = (product && product.meta && product.meta.supportRoles) || [];
    return (
      roles.indexOf('barrier') !== -1 ||
      roles.indexOf('soothing') !== -1 ||
      roles.indexOf('hydrating') !== -1
    );
  }

  function indexPoolByStep(pool) {
    const byStep = {};
    STEPS.forEach((step) => {
      byStep[step] = [];
    });
    (pool || []).forEach((p) => {
      if (!p || !p.meta || !p.meta.step || !byStep[p.meta.step]) return;
      byStep[p.meta.step].push(p);
    });
    return byStep;
  }

  function rankSerumAnchors(byStep, skin, concern) {
    const serums = byStep.serum || [];
    const ranked = [];
    serums.forEach((p) => {
      if (!isHardEligible(p, 'serum', skin, concern)) return;
      if (!serumSupportsConcern(p, concern)) return;
      const fit = productFit(p, 'serum', skin, concern, {}, {});
      if (!isFinite(fit.score) || fit.score < 40) return;
      ranked.push({ product: p, score: fit.score, concernFit: concernEvidence(p, concern).fit });
    });
    /**
     * 2026-08-07 (user-reported): two serums can tie on both score and
     * concernFit (e.g. one tagged Primary via "Dark Spots", another via
     * "Uneven Tone" — both map into the same quiz concern bucket). The old
     * sort then fell back to whatever order the Shopify catalog happened to
     * return products in — arbitrary, not a clinical judgment. As the
     * anchor IS the routine's main treatment, a genuine tie should go to
     * the more targeted/concentrated formula: potency "active" over
     * "gentle" (e.g. a dedicated 15% niacinamide + TXA serum over a gentler
     * blended-complex one at the same concern tier).
     */
    ranked.sort((a, b) => {
      return (
        b.score - a.score ||
        b.concernFit - a.concernFit ||
        (b.product.meta.strength === 'active' ? 1 : 0) - (a.product.meta.strength === 'active' ? 1 : 0)
      );
    });
    // Prefer clinically distinct anchors in the shortlist.
    const picked = [];
    const seenPaths = {};
    ranked.forEach((row) => {
      if (picked.length >= ANCHOR_CAP) return;
      const path = clinicalPath([{ step: 'serum', product: row.product }]);
      if (seenPaths[path] && picked.length >= 3) return;
      seenPaths[path] = true;
      picked.push(row);
    });
    if (picked.length < Math.min(5, ranked.length)) {
      ranked.forEach((row) => {
        if (picked.length >= ANCHOR_CAP) return;
        if (picked.indexOf(row) !== -1) return;
        picked.push(row);
      });
    }
    return picked;
  }

  function isLightMoisturizerTexture(product) {
    const t = product && product.meta && product.meta.texture;
    return t === 'gel' || t === 'watery';
  }

  /**
   * Heavy cream on oily/combo — aligned with QA lightOk:
   * title signals a light finish, OR pore-control actives (niacinamide/BHA/…).
   * Brightening-only creams (Vit C cream) stay out for oily/combo daily routines.
   */
  function oilyCreamMoisturizerAllowed(product) {
    if (!product || !product.meta) return false;
    const title = normalize(product.title || '');
    if (/gel|water|lotion|jelly|oil.?free|لوشن|جل|خفيف|emulsion/.test(title)) return true;
    const fams = product.meta.nameActives || product.meta.activeFamilies || [];
    return fams.some(function (family) {
      return (
        family === 'niacinamide' ||
        family === 'salicylic' ||
        family === 'azelaic' ||
        family === 'tea-tree' ||
        family === 'succinic'
      );
    });
  }

  function supportPickPasses(cand, step, skin, opts) {
    if (!cand || !cand.product) return false;
    const fams = treatmentActiveFamilies(cand.product, step);
    const blocked = opts.blockedActiveFamilies || [];
    // Toner is exempt from the mild-family repeat cap (see hasBlockedHarshActive's
    // allowMildRepeat note): the band is score-sorted, so this just lets the
    // strongest-fit relevant toner win on its merits instead of losing a
    // clinically-stronger repeat pick to a weaker-fit, different-family one
    // purely because niacinamide (or another mild family) already appeared
    // twice in serum + moisturizer — harsh-family blocking and the intensity
    // budget below are unaffected either way.
    if (hasBlockedHarshActive(fams, blocked, step === 'toner' || !!opts.relaxMildCap)) return false;
    const budget = skin === 'sensitive' ? 2 : skin === 'dry' ? 3 : 4;
    const addIntensity = activeIntensity(cand.product, step);
    if (addIntensity > 0 && (opts.activeIntensity || 0) + addIntensity > budget) return false;
    // Sensitive: no Active toners/pads in the daily 5-step (Phase E expert).
    if (skin === 'sensitive' && step === 'toner' && cand.product.meta.strength === 'active') {
      return false;
    }
    // 2026-08-07: only cross-stack with another HARSH active (benzoyl/salicylic
    // itself) vetoes a BHA/exfoliating toner outright. azelaic and tea-tree are
    // mild (intensity 1) — they used to sit in this blocklist too, which vetoed
    // every salicylic-based acne toner whenever the anchor serum was an azelaic
    // serum (Purito/Anua azelaic serums dominate the oily/combo+acne anchor
    // shortlist), collapsing the toner slot to a single surviving product
    // (Anua Azelaic+Cica toner) duplicated across alternate routines. The
    // graduated activeIntensity budget (above) already governs mild-active
    // stacking; this rule now only guards the genuinely harsh combo.
    if (
      step === 'toner' &&
      fams.some((f) => f === 'salicylic' || f === 'exfoliating-acid') &&
      (opts.blockedActiveFamilies || []).some(function (f) {
        return f === 'benzoyl' || f === 'salicylic';
      })
    ) {
      return false;
    }
    return true;
  }

  /**
   * Support queue (cleanser / moisturizer / sunscreen) — plan §5ب:
   * 1) unused skin∩concern (aligned) first — priority, not forever lock
   * 2) then unused skin-only (e.g. other light oily moisturizers)
   * 3) only after both tiers are exhausted, loop/reuse
   */
  function pickSupportFromQueue(step, skin, concern, stepPool, usedIds, usedBrands, opts, consumed) {
    const needBarrier =
      (skin === 'dry' || skin === 'sensitive' || concern === 'dryness') && step === 'moisturizer';
    const ranked = [];
    (stepPool || []).forEach((p) => {
      if (usedIds.has(p.id)) return;
      if (!isHardEligible(p, step, skin, concern)) return;
      const fit = productFit(p, step, skin, concern, usedBrands, opts);
      if (!isFinite(fit.score) || fit.score <= -80) return;
      let score = fit.score;
      if (needBarrier) {
        if (hasBarrierRole(p)) score += 24;
        else score -= 12;
        if (p.meta.strength === 'active') score -= 18;
      }
      // Oily/combo moisturizers: light textures stay preferred in ranking.
      if (step === 'moisturizer' && (skin === 'oily' || skin === 'combination')) {
        if (isLightMoisturizerTexture(p)) score += 10;
        else if (p.meta.texture === 'cream') score -= 12;
      }
      // Pigmentation: brightening moisturizers (TXA/niacinamide creams) must beat
      // generic gels that only hydrate — otherwise Celimax/AXIS/SKIN1004 never show.
      //
      // 2026-08-07 (round 5, user-directed): moisturizer is picked BEFORE toner, so
      // this bonus used to reward ANY brightening family match — including one that
      // just duplicates the serum's own family (e.g. a niacinamide cream under a
      // niacinamide serum). That reuse spends the shared duplicate-active budget
      // (hasBlockedHarshActive caps a mild family at 2 occurrences) before toner
      // ever gets a turn, starving toner down to whichever single family never
      // overlapped (azelaic, for oily×pigmentation — confirmed live: 3/5 routines).
      // The family-match bonus now only fires for a NOVEL family the serum hasn't
      // already used — a moisturizer that only restates the serum's active isn't
      // adding real treatment breadth, and rewarding it anyway crowds out a
      // different-family option that would leave the toner's slot free.
      if (step === 'moisturizer' && concern === 'pigmentation') {
        const pigFit = concernEvidence(p, 'pigmentation').fit;
        if (pigFit >= 0.55) score += 28;
        if (pigFit >= 1) score += 12;
        const blockedFamilies = opts.blockedActiveFamilies || [];
        const brighteningFamilies = ['niacinamide', 'tranexamic', 'vitamin-c', 'arbutin', 'kojic', 'glutathione'];
        const candidateFamilies = p.meta.nameActives || p.meta.activeFamilies || [];
        const hasNovelBrightening = candidateFamilies.some(function (f) {
          return brighteningFamilies.indexOf(f) !== -1 && blockedFamilies.indexOf(f) === -1;
        });
        if (hasNovelBrightening) {
          score += 16;
          if (p.meta.texture === 'cream' && (skin === 'oily' || skin === 'combination')) {
            score += 10; // undo part of cream penalty for true brightening creams
          }
        }
      }
      // 2026-08-07: at comparable concern-fit, prefer the gentler/more comfortable
      // daily pick for moisturizer + sunscreen — never sacrifice comfort for concern.
      if ((step === 'moisturizer' || step === 'sunscreen') && p.meta.strength === 'gentle') {
        score += 6;
      }
      ranked.push({ product: p, score: score, baseScore: fit.score, meta: fit });
    });
    ranked.sort((a, b) => b.score - a.score);

    const skinPool = ranked.filter((r) => skinAffinity(r.product, skin, step, concern) >= 0.45);
    const base = skinPool.length ? skinPool : ranked;

    /**
     * 2026-08-07 audit fix — graduated concern-relevance priority.
     * Old order (aligned-fresh > skin-fresh > loop-aligned > loop-skin) let an
     * exhausted concern pool fall straight to "any skin-compatible product",
     * which surfaced a wrinkles/dehydration toner in a pores routine. Routine
     * identity comes from the serum anchor (plan decision), so reusing a
     * genuinely relevant support product across routines is fine — it should
     * rank ABOVE an unused-but-irrelevant one. Relevance now outranks
     * freshness; freshness only breaks ties within the same relevance tier.
     * Cleanser outside pores/acne intentionally skips concern-priority
     * entirely (rinse-off contact time; see stepWantsConcernPriority) and
     * falls straight to skin-fit ranking with only the light score bonus
     * already baked into `ranked`.
     */
    let queue;
    let tier;
    // Hoisted (not just branch-local): the band-scan below still reads these
    // to decide whether a consumed id may be reused while fresher options exist.
    let alignedFresh = [];
    let skinFresh = [];
    if (!stepWantsConcernPriority(step, concern)) {
      const baseFresh = base.filter((r) => !consumed.has(r.product.id));
      skinFresh = baseFresh;
      if (baseFresh.length) {
        queue = baseFresh;
        tier = 'skin';
      } else {
        queue = base.slice();
        tier = 'loop-skin';
      }
    } else {
      const aligned = base.filter((r) => concernEvidence(r.product, concern).fit >= 0.55);
      const loose = base.filter((r) => {
        const fit = concernEvidence(r.product, concern).fit;
        return fit > 0 && fit < 0.55;
      });
      alignedFresh = aligned.filter((r) => !consumed.has(r.product.id));
      const looseFresh = loose.filter((r) => !consumed.has(r.product.id));
      skinFresh = base.filter((r) => !consumed.has(r.product.id));

      /**
       * 2026-08-07 (round 2 — variety fix, user-tested): the old order tried
       * every relevance tier's REUSE before ever trying a fresh-but-less-
       * relevant option, so a thin pool (e.g. 1 wrinkles-tagged sunscreen, or
       * 3 pore-tagged moisturizers) repeated the SAME product(s) across all 5
       * routines. Confirmed by live testing that toner/cleanser variety was
       * already good — only moisturizer and sunscreen needed this. Three
       * distinct orders:
       * - sunscreen: not hard-required (stepRequiresConcernOrReject excludes
       *   it) — maximize variety outright: any unused skin-appropriate
       *   sunscreen beats repeating an already-shown concern-tagged one.
       * - moisturizer: hard-required, so still guarantee relevance — but try
       *   fresh loose-tier (Secondary Support) before reusing an aligned
       *   (Primary) one. Never jump to skin-only before reuse here, since an
       *   irrelevant pick would fail stepMissesConcernHard and drop the
       *   whole routine instead of just costing variety.
       * - toner (and cleanser-for-pores/acne): unchanged — already correct.
       */
      if (step === 'sunscreen') {
        if (alignedFresh.length) {
          queue = alignedFresh;
          tier = 'aligned';
        } else if (looseFresh.length) {
          queue = looseFresh;
          tier = 'loose';
        } else if (skinFresh.length) {
          queue = skinFresh;
          tier = 'skin';
        } else if (aligned.length) {
          queue = aligned.slice();
          tier = 'loop-aligned';
        } else if (loose.length) {
          queue = loose.slice();
          tier = 'loop-loose';
        } else {
          queue = base.slice();
          tier = 'loop-skin';
        }
      } else if (step === 'moisturizer') {
        // Reverted to the original (toner-matching) order — testing showed
        // reordering this one shifted routineFit scores enough to change
        // which candidates survived downstream MMR/quality-band filtering
        // for several unrelated pairs (acne pool especially). Sunscreen
        // above gets the variety fix since it carries no rejection risk;
        // moisturizer's 3-way rotation was reported as acceptable, not
        // broken, so leave its proven-safe order alone.
        if (alignedFresh.length) {
          queue = alignedFresh;
          tier = 'aligned';
        } else if (aligned.length) {
          queue = aligned.slice();
          tier = 'loop-aligned';
        } else if (looseFresh.length) {
          queue = looseFresh;
          tier = 'loose';
        } else if (loose.length) {
          queue = loose.slice();
          tier = 'loop-loose';
        } else if (skinFresh.length) {
          queue = skinFresh;
          tier = 'skin';
        } else {
          queue = base.slice();
          tier = 'loop-skin';
        }
      } else if (alignedFresh.length) {
        queue = alignedFresh;
        tier = 'aligned';
      } else if (aligned.length) {
        queue = aligned.slice();
        tier = 'loop-aligned';
      } else if (looseFresh.length) {
        queue = looseFresh;
        tier = 'loose';
      } else if (loose.length) {
        queue = loose.slice();
        tier = 'loop-loose';
      } else if (skinFresh.length) {
        queue = skinFresh;
        tier = 'skin';
      } else {
        queue = base.slice();
        tier = 'loop-skin';
      }
    }

    if (step === 'moisturizer' && (skin === 'oily' || skin === 'combination') && tier.indexOf('skin') === 0) {
      queue = queue.slice().sort(function (a, b) {
        const la = isLightMoisturizerTexture(a.product) ? 1 : 0;
        const lb = isLightMoisturizerTexture(b.product) ? 1 : 0;
        return lb - la || b.score - a.score;
      });
    }

    // Sensitive / dry acne-pores: recycle gentle toners — never fall into Active pads (Phase E).
    if (
      step === 'toner' &&
      (skin === 'sensitive' || (skin === 'dry' && (concern === 'acne' || concern === 'pores')))
    ) {
      const gentle = base.filter(function (r) {
        return r.product.meta.strength !== 'active';
      });
      const gentleFresh = gentle.filter(function (r) {
        return !consumed.has(r.product.id);
      });
      if (gentleFresh.length) {
        queue = gentleFresh;
        tier = 'gentle-sensitive';
      } else if (gentle.length) {
        queue = gentle.slice();
        tier = 'loop-gentle';
      }
    }

    // Loop tier: rotate LRU — don't always re-pick the single top SKU (R1/R3/R4/R5 bug).
    if (tier.indexOf('loop') === 0 && consumed && consumed.size) {
      const order = [];
      consumed.forEach(function (id) {
        order.push(id);
      });
      queue = queue.slice().sort(function (a, b) {
        const ia = order.indexOf(a.product.id);
        const ib = order.indexOf(b.product.id);
        const ra = ia === -1 ? 9999 : ia;
        const rb = ib === -1 ? 9999 : ib;
        return ra - rb || b.score - a.score;
      });
    }

    /**
     * 2026-08-07 audit fix: this used to skip a consumed-but-relevant band[0]
     * "while unused options remain" — checking `alignedFresh.length ||
     * skinFresh.length` regardless of whether those unused options were
     * actually relevant. That silently fell through to the tier-blind
     * "any unused candidate" block below, which is how a routine's ONLY
     * acne-relevant moisturizer got skipped in favor of a fresh-but-irrelevant
     * one once a prior routine had consumed it. Tier selection above already
     * picked the right pool (relevance before freshness); the band scan's
     * only remaining job is the stacking/budget check.
     */
    const band = queue.slice(0, Math.max(SUPPORT_TIER_K, Math.min(12, queue.length)));
    for (let i = 0; i < band.length; i++) {
      const cand = band[i];
      if (!supportPickPasses(cand, step, skin, opts)) continue;
      consumed.add(cand.product.id);
      cand.supportTier = tier;
      return cand;
    }

    // Last resort: nothing in the selected tier passed stacking/budget —
    // widen to any unused candidate from base (still skin-compatible).
    for (let i = 0; i < base.length; i++) {
      const cand = base[i];
      if (consumed.has(cand.product.id)) continue;
      if (!supportPickPasses(cand, step, skin, opts)) continue;
      consumed.add(cand.product.id);
      cand.supportTier = 'fallback';
      return cand;
    }

    // True loop: reuse LRU from band that passes stacking checks.
    for (let i = 0; i < band.length; i++) {
      const cand = band[i];
      if (!supportPickPasses(cand, step, skin, opts)) continue;
      consumed.add(cand.product.id);
      cand.supportTier = tier;
      return cand;
    }

    if (band[0]) {
      consumed.add(band[0].product.id);
      band[0].supportTier = tier;
      return band[0];
    }
    return { product: null, score: -Infinity, meta: null };
  }

  function rowsFromPicked(picked, concern) {
    return STEPS.map((step, idx) => {
      const row = picked[step];
      if (!row || !row.product) return null;
      return {
        step: step,
        index: idx + 1,
        label: boot.labels[step],
        product: row.product,
        score: row.score,
        selectionRank: row.selectionRank || null,
        fallback: false,
        whyAnchor: step === 'serum' ? whyAnchor(row.product, concern) : ''
      };
    }).filter(Boolean);
  }

  /** Build one full routine around a locked serum anchor (Phase C spine). */
  function buildRoutineAroundAnchor(skin, concern, serumProduct, byStep, consumedQueues) {
    const usedIds = new Set([serumProduct.id]);
    const usedBrands = {};
    const usedActiveFamilies = [];
    const activeState = { intensity: 0 };
    const picked = {};

    const serumFit = productFit(serumProduct, 'serum', skin, concern, usedBrands, {});
    picked.serum = {
      product: serumProduct,
      score: serumFit.score,
      meta: serumFit,
      selectionRank: 1
    };
    treatmentActiveFamilies(serumProduct, 'serum').forEach((family) => {
      usedActiveFamilies.push(family);
    });
    activeState.intensity += activeIntensity(serumProduct, 'serum');
    const sb = brandFamily(serumProduct);
    if (sb) usedBrands[sb] = 1;

    // Moisturizer before toner: mild brightening creams (AXIS/Celimax) must lock
    // while intensity budget remains. Picking Anua Peach toner (intensity 2) first
    // after a kojic/niacinamide serum filled the oily budget and forced generic gels.
    // Toner keeps supportiveOnly ranking with soft demote on harsh actives.
    ['moisturizer', 'toner', 'cleanser', 'sunscreen'].forEach((step) => {
      const floor =
        step === 'toner' ? 10 : step === 'sunscreen' ? 20 : step === 'cleanser' ? 5 : -20;
      const opts = {
        supportiveOnly: step === 'toner',
        supportiveMoisturizer: step === 'moisturizer',
        blockedActiveFamilies: usedActiveFamilies.slice(),
        activeIntensity: activeState.intensity
      };
      if (!consumedQueues[step]) consumedQueues[step] = new Set();
      const pick = pickSupportFromQueue(
        step,
        skin,
        concern,
        byStep[step] || [],
        usedIds,
        usedBrands,
        opts,
        consumedQueues[step]
      );
      commitPick(picked, usedIds, usedBrands, usedActiveFamilies, activeState, step, pick, floor);
    });

    // Fill gaps with broad eligibility (still no random).
    STEPS.forEach((step) => {
      if (picked[step]) return;
      const opts = {
        supportiveOnly: step === 'toner',
        supportiveMoisturizer: step === 'moisturizer',
        blockedActiveFamilies: usedActiveFamilies.slice(),
        activeIntensity: activeState.intensity
      };
      const fill = pickBest(byStep[step] || [], step, skin, concern, usedIds, usedBrands, opts);
      const floor = step === 'serum' ? 40 : step === 'toner' ? 5 : step === 'sunscreen' ? 15 : -40;
      commitPick(picked, usedIds, usedBrands, usedActiveFamilies, activeState, step, fill, floor);
    });

    const rows = rowsFromPicked(picked, concern);
    const serumRow = rows.find((r) => r.step === 'serum');
    if (serumRow) serumRow.whyAnchor = whyAnchor(serumProduct, concern);
    return rows;
  }

  /**
   * 2026-08-07 audit fix: `samePath` used to be only a soft penalty term, so a
   * thin anchor pool (e.g. sensitive×acne) could select two routines with the
   * exact same clinical path (same primary active family, different brand
   * only) and present the second as a genuine "alternative". A different
   * clinical path is now a hard requirement (pass 1) whenever any candidate
   * with an unused path remains; duplicate paths are only ever accepted as an
   * explicit last resort (pass 2) when the pool is truly out of distinct
   * strategies — matching plan decision "reject an alternative that only
   * changed brand but kept the same path".
   */
  function mmrSelectRoutines(candidates, maxN) {
    const selected = [];
    const remaining = candidates.slice().sort((a, b) => b.fit.score - a.fit.score);
    const usedSerumIds = Object.create(null);
    const usedPaths = Object.create(null);

    function similarityAgainstSelected(c, includeSamePath) {
      let sim = 0;
      for (let j = 0; j < selected.length; j++) {
        const s = selected[j];
        const overlap = c.rows.filter((r) => s.rows.some((x) => x.product.id === r.product.id)).length;
        const jac = familyJaccard(c.rows, s.rows);
        const samePath = includeSamePath && clinicalPath(c.rows) === clinicalPath(s.rows) ? 1 : 0;
        const stepPenalty = routineStepDiff(c.rows, s.rows) < 2 ? 1 : 0;
        sim = Math.max(sim, overlap * 0.28 + jac * 0.4 + samePath * 0.45 + stepPenalty * 0.35);
      }
      return sim;
    }

    while (selected.length < maxN && remaining.length) {
      let bestIdx = -1;
      let bestVal = -Infinity;

      // Pass 1 — hard diversity floor: only candidates with a clinical path
      // not already selected are eligible.
      for (let i = 0; i < remaining.length; i++) {
        const c = remaining[i];
        const serum = rowForStep(c.rows, 'serum');
        const serumId = serum && serum.product ? serum.product.id : null;
        // Hard rule: never pick two routines with the same serum anchor (R1=R4 bug).
        if (serumId != null && usedSerumIds[serumId]) continue;
        if (usedPaths[clinicalPath(c.rows)]) continue;
        const val = c.fit.score - 36 * similarityAgainstSelected(c, false);
        if (val > bestVal) {
          bestVal = val;
          bestIdx = i;
        }
      }

      // Pass 2 — true last resort: no distinct-path candidate left. Fall back
      // to the original soft scoring (duplicate paths allowed, penalized).
      if (bestIdx < 0) {
        for (let i = 0; i < remaining.length; i++) {
          const c = remaining[i];
          const serum = rowForStep(c.rows, 'serum');
          const serumId = serum && serum.product ? serum.product.id : null;
          if (serumId != null && usedSerumIds[serumId]) continue;
          const val = c.fit.score - 36 * similarityAgainstSelected(c, true);
          if (val > bestVal) {
            bestVal = val;
            bestIdx = i;
          }
        }
      }

      if (bestIdx < 0) break;
      const picked = remaining.splice(bestIdx, 1)[0];
      const pickedSerum = rowForStep(picked.rows, 'serum');
      if (pickedSerum && pickedSerum.product) usedSerumIds[pickedSerum.product.id] = true;
      usedPaths[clinicalPath(picked.rows)] = true;
      selected.push(picked);
    }
    return selected;
  }

  function rowForStep(rows, step) {
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].step === step) return rows[i];
    }
    return null;
  }

  /**
   * After MMR, break stuck support SKUs on R2+ only (never touch R1 — accuracy).
   * Prefer higher-scoring alternates already seen in the candidate pool.
   */
  function diversifySupportAcrossList(selected, allCandidates, steps, skin, concern) {
    if (!selected || selected.length < 2) return selected;
    skin = skin || state.skin;
    concern = concern || state.concern;

    const poolByStep = Object.create(null);
    steps.forEach(function (step) {
      poolByStep[step] = [];
    });
    allCandidates.forEach(function (c) {
      steps.forEach(function (step) {
        const row = rowForStep(c.rows, step);
        if (!row || !row.product) return;
        if (poolByStep[step].some(function (x) {
          return x.product.id === row.product.id;
        })) {
          return;
        }
        poolByStep[step].push(row);
      });
    });
    steps.forEach(function (step) {
      poolByStep[step].sort(function (a, b) {
        return (b.score || 0) - (a.score || 0);
      });
    });

    const usedByStep = Object.create(null);
    steps.forEach(function (step) {
      usedByStep[step] = Object.create(null);
    });

    return selected.map(function (entry, idx) {
      // Lock routine #1 exactly as scored — diversity only from R2 onward.
      if (idx === 0) {
        steps.forEach(function (step) {
          const row = rowForStep(entry.rows, step);
          if (row && row.product) usedByStep[step][row.product.id] = true;
        });
        return entry;
      }

      let rows = entry.rows.slice();
      let changed = false;

      // Rebuild leave-on budget / harsh families from the locked serum (+ kept steps)
      // so diversity swaps cannot reintroduce acid/retinoid stacks the builder forbade.
      function supportOptsFromRows(currentRows, forStep) {
        // Multiset (duplicates kept) — see hasBlockedHarshActive's 2026-08-07 note.
        const blocked = [];
        let intensity = 0;
        const serum = rowForStep(currentRows, 'serum');
        if (serum && serum.product) {
          treatmentActiveFamilies(serum.product, 'serum').forEach(function (family) {
            blocked.push(family);
          });
          intensity += activeIntensity(serum.product, 'serum');
        }
        ['toner', 'cleanser', 'moisturizer', 'sunscreen'].forEach(function (other) {
          if (other === forStep) return;
          const row = rowForStep(currentRows, other);
          if (!row || !row.product) return;
          treatmentActiveFamilies(row.product, other).forEach(function (family) {
            blocked.push(family);
          });
          intensity += activeIntensity(row.product, other);
        });
        return {
          supportiveOnly: forStep === 'toner',
          supportiveMoisturizer: forStep === 'moisturizer',
          blockedActiveFamilies: blocked,
          activeIntensity: intensity
        };
      }

      steps.forEach(function (step) {
        const cur = rowForStep(rows, step);
        if (!cur || !cur.product) return;

        if (!usedByStep[step][cur.product.id]) {
          usedByStep[step][cur.product.id] = true;
          return;
        }

        const pool = poolByStep[step] || [];
        const opts = supportOptsFromRows(rows, step);
        let swapped = null;
        for (let i = 0; i < pool.length; i++) {
          const alt = pool[i];
          if (usedByStep[step][alt.product.id]) continue;
          if (!supportPickPasses(alt, step, skin, opts)) continue;
          const fit = productFit(alt.product, step, skin, concern, {}, opts);
          if (!isFinite(fit.score) || fit.score <= -80) continue;
          // 2026-08-07 audit fix: never diversify INTO a concern-irrelevant
          // pick for a step that requires one (toner/moisturizer). Reusing
          // the correct item across routines is fine — routine identity
          // comes from the serum anchor, not from every support item being
          // unique — so a fresh-but-irrelevant swap is not an improvement.
          // This is exactly how a "pores" routine's reused-but-relevant
          // toner got swapped for a pigmentation toner, which then failed
          // the readiness check and dropped the whole routine.
          if (stepMissesConcernHard({ step: step, product: alt.product }, skin, concern)) continue;
          swapped = alt;
          break;
        }
        if (!swapped) {
          usedByStep[step][cur.product.id] = true;
          return;
        }

        rows = rows.map(function (r) {
          return r.step === step ? Object.assign({}, swapped) : r;
        });
        usedByStep[step][swapped.product.id] = true;
        changed = true;
      });

      if (!changed) return entry;
      return {
        rows: rows,
        fit: routineFit(rows, skin, concern),
        clinicalPath: clinicalPath(rows),
        whyAnchor: entry.whyAnchor
      };
    });
  }

  /**
   * Phase C: build the full alternative list once (anchors → routines → MMR).
   * Shuffle only advances the index — no full rebuild (Perf-Routine).
   */
  function ensureRoutineList(skin, concern) {
    const key = skin + ':' + concern;
    if (state.routineListKey === key && state.routineList && state.routineList.length) {
      return state.routineList;
    }
    const t0 = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    const pool = state.products.filter(
      (p) => p.meta && p.meta.step && !p.meta.excluded && p.available && p.variantId
    );
    const byStep = indexPoolByStep(pool);
    const anchors = rankSerumAnchors(byStep, skin, concern);

    /**
     * 2026-08-07 audit fix: stepMissesConcernHard rejects a routine outright
     * when its toner/moisturizer has zero concern relevance — but for some
     * skin×concern pairs the catalog has NO eligible, concern-relevant option
     * for that step at all (e.g. oily×wrinkles: no wrinkles-tagged toner is
     * also oily-compatible). Rejecting every candidate there zeroed the whole
     * pair. The hard check only makes sense when a better option genuinely
     * exists and got bypassed — so record per-pair availability once here and
     * have the check stand down when the catalog truly has nothing to offer.
     */
    // Must match the real selection funnel in pickSupportFromQueue (isHardEligible
    // + skinAffinity>=0.45 build the `base` pool candidates are actually drawn
    // from), not just isHardEligible alone — isHardEligible allows any skin label
    // on toner, but skinAffinity still penalizes an unrelated one, so an
    // "eligible" toner can be practically unreachable. Using the looser check
    // first reported oily×wrinkles as "available" while the real pipeline could
    // never reach any of those 4 candidates (all skinAffinity < 0.45 for oily).
    function isPracticallyReachable(p, step) {
      return isHardEligible(p, step, skin, concern) && skinAffinity(p, skin, step, concern) >= 0.45 && serumSupportsConcern(p, concern);
    }
    state.concernAvailability = {
      toner: (byStep.toner || []).some((p) => isPracticallyReachable(p, 'toner')),
      moisturizer: (byStep.moisturizer || []).some((p) => isPracticallyReachable(p, 'moisturizer'))
    };

    const candidates = [];
    const seen = {};

    anchors.forEach((anchor) => {
      // Fresh support queues per serum — shared consume-before-MMR starved later
      // anchors of AXIS Glow / Anua Peach / Celimax, then MMR promoted those weak
      // shells to R1. Cross-routine uniqueness is handled after MMR.
      const consumedQueues = {
        toner: new Set(),
        cleanser: new Set(),
        moisturizer: new Set(),
        sunscreen: new Set()
      };
      const rows = buildRoutineAroundAnchor(skin, concern, anchor.product, byStep, consumedQueues);
      if (!rows.length) return;
      const fit = routineFit(rows, skin, concern);
      if (fit.reject) return;
      // Prefer routines the UI can actually show (hard readiness).
      const prevSkin = state.skin;
      const prevConcern = state.concern;
      state.skin = skin;
      state.concern = concern;
      const displayOk = isRoutineReady(rows);
      state.skin = prevSkin;
      state.concern = prevConcern;
      if (!displayOk) return;
      const idKey = rows.map((r) => r.product.id).join(',');
      if (seen[idKey]) return;
      seen[idKey] = true;
      candidates.push({
        rows: rows,
        fit: fit,
        clinicalPath: clinicalPath(rows),
        whyAnchor: whyAnchor(anchor.product, concern)
      });
    });

    // Quality band near the best, then dual MMR (products + clinical path).
    candidates.sort((a, b) => b.fit.score - a.fit.score);
    const bestScore = candidates.length ? candidates[0].fit.score : -Infinity;
    // Wider band so brightening alternatives aren't cut before MMR (was −22 → too tight).
    const band = candidates.filter((c) => c.fit.score >= bestScore - 40);
    const poolForMmr = band.length >= 5 ? band : candidates;
    let selected = mmrSelectRoutines(poolForMmr, Math.min(ROUTINE_LIST_MAX, 5));

    // Ensure at least one routine if anything built (even weaker).
    if (!selected.length && candidates.length) selected = candidates.slice(0, 1);

    // Rebuild support around the MMR-locked serums with a shared consume queue.
    // Candidate-gen used fresh queues (correct per-serum quality for scoring), so
    // every shell often shared AXIS Glow cream — list diversity must happen here.
    if (selected.length) {
      const listQueues = {
        toner: new Set(),
        cleanser: new Set(),
        moisturizer: new Set(),
        sunscreen: new Set()
      };
      selected = selected.map(function (entry) {
        const serumRow = rowForStep(entry.rows, 'serum');
        if (!serumRow || !serumRow.product) return entry;
        const rows = buildRoutineAroundAnchor(skin, concern, serumRow.product, byStep, listQueues);
        if (!rows.length) return entry;
        return {
          rows: rows,
          fit: routineFit(rows, skin, concern),
          clinicalPath: clinicalPath(rows),
          whyAnchor: whyAnchor(serumRow.product, concern)
        };
      });
    }

    // Safety net: if rebuild still collides on support, swap from the wider candidate pool.
    selected = diversifySupportAcrossList(
      selected,
      candidates,
      ['toner', 'moisturizer', 'cleanser', 'sunscreen'],
      skin,
      concern
    );
    const deduped = [];
    const seenSerum = Object.create(null);
    const seenFull = Object.create(null);
    selected.forEach(function (entry) {
      const serum = rowForStep(entry.rows, 'serum');
      const sid = serum && serum.product ? serum.product.id : null;
      const full = entry.rows.map(function (r) {
        return r.product.id;
      }).join(',');
      if (seenFull[full]) return;
      if (sid != null && seenSerum[sid]) return;
      seenFull[full] = true;
      if (sid != null) seenSerum[sid] = true;
      deduped.push(entry);
    });
    selected = deduped;

    // 2026-08-07 audit fix: the support rebuild above (shared listQueues) can
    // still land on a hard-blocked pick (e.g. zero concern relevance) even
    // though the pre-MMR candidate passed isRoutineReady with fresh queues.
    // Re-validate post-rebuild and drop anything that no longer qualifies —
    // fewer good routines beats one bad one padded in to hit the count.
    {
      const prevSkin = state.skin;
      const prevConcern = state.concern;
      state.skin = skin;
      state.concern = concern;
      selected = selected.filter(function (entry) {
        return isRoutineReady(entry.rows);
      });
      state.skin = prevSkin;
      state.concern = prevConcern;
    }

    /**
     * 2026-08-07 (user-reported): mmrSelectRoutines picks greedily trading
     * score for diversity at each step, so the SELECTION order is not a
     * quality order — a routine scoring 79 could land at position 3 while
     * ones scoring 155 and 171 sit at 4 and 5. MMR's job is choosing WHICH
     * 5 distinct routines make the list; it should not also dictate reveal
     * order. Re-sort by actual quality now that rebuild/diversify finished
     * (their scores can shift from the pre-rebuild selection order), so R1
     * is genuinely the strongest and weaker-but-still-valid diverse options
     * (like a Secondary-only concern match) surface last, where a shopper
     * reads them as "last resort" rather than "second best".
     */
    selected.sort(function (a, b) {
      return (b.fit && b.fit.score || 0) - (a.fit && a.fit.score || 0);
    });

    state.routineList = selected;
    state.routineListKey = key;
    state.routineIndex = 0;
    state.lastBuildMs =
      (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - t0;
    return state.routineList;
  }

  function buildRoutineCandidate(skin, concern, recentIds, avoidSerumIds) {
    // Legacy single-candidate path kept for pregnancy swap probes.
    const pool = state.products.filter(
      (p) => p.meta && p.meta.step && !p.meta.excluded && p.available && p.variantId
    );
    const byStep = indexPoolByStep(pool);
    let anchors = rankSerumAnchors(byStep, skin, concern);
    if (avoidSerumIds && avoidSerumIds.length) {
      const filtered = anchors.filter((a) => avoidSerumIds.indexOf(a.product.id) === -1);
      if (filtered.length) anchors = filtered;
    }
    if (!anchors.length) return [];
    const queues = {
      cleanser: new Set(),
      moisturizer: new Set(),
      sunscreen: new Set()
    };
    return buildRoutineAroundAnchor(skin, concern, anchors[0].product, byStep, queues);
  }

  /**
   * ------------------------------------------------------- ROUTINE-LEVEL SCORE
   * Per-product scores answer "is this a good serum for this customer?".
   * Nothing previously asked "is this a good routine?", so five individually
   * sensible picks could still duplicate the same acid three times or leave a
   * strong treatment with no barrier support. This is that missing question.
   */
  function scoreRoutine(rows, skin, concern) {
    const notes = [];
    if (!rows || !rows.length) return { score: -Infinity, notes: ['empty'] };

    // Carry the per-step quality through, averaged so routine length doesn't dominate.
    let score = rows.reduce((sum, row) => sum + row.score, 0) / rows.length;

    // Completeness: an incomplete routine is not a routine.
    const missing = STEPS.length - rows.length;
    if (missing > 0) {
      score -= 60 * missing;
      notes.push('missing:' + missing);
    }

    // The serum is the treatment anchor. Averaging it in with four supporting
    // products would let a weak anchor hide behind a tidy routine.
    const serumRow = rows.find((row) => row.step === 'serum');
    const serumFit = serumRow ? concernEvidence(serumRow.product, concern).fit : 0;
    score += 34 * serumFit;
    if (serumRow && serumFit < 0.4) {
      score -= 26;
      notes.push('weak-anchor');
    }

    // Concern coverage. Two or three steps working on the concern is a routine;
    // one is a lone product and five is an over-treated face.
    const onConcern = rows.filter((row) => concernEvidence(row.product, concern).fit >= 0.35).length;
    if (onConcern === 0) {
      score -= 45;
      notes.push('coverage:none');
    } else if (onConcern === 1) {
      score -= 12;
      notes.push('coverage:thin');
    } else if (onConcern <= 3) {
      score += 14;
      notes.push('coverage:good');
    } else {
      score -= 10 * (onConcern - 3);
      notes.push('coverage:excessive');
    }

    // Redundancy: paying twice for the same active is the clearest sign the
    // routine was assembled one step at a time.
    const familyCount = {};
    rows.forEach((row) => {
      (row.product.meta.nameActives || []).forEach((family) => {
        familyCount[family] = (familyCount[family] || 0) + 1;
      });
    });
    let duplicated = 0;
    Object.keys(familyCount).forEach((family) => {
      if (familyCount[family] > 1) duplicated += familyCount[family] - 1;
    });
    if (duplicated) {
      score -= 22 * duplicated;
      notes.push('duplicate-active:' + duplicated);
    }

    // Irritation budget on leave-ons only (toner/serum/moisturizer).
    const intensity = rows.reduce(
      (sum, row) => sum + activeIntensity(row.product, row.step),
      0
    );
    const budget = skin === 'sensitive' ? 2 : skin === 'dry' ? 3 : 4;
    if (intensity > budget) {
      score -= 30 * (intensity - budget);
      notes.push('irritation:over');
    }

    // A potent routine needs something that repairs. This is the pairing an
    // experienced consultant makes automatically.
    const soothingOrBarrier = rows.filter((row) => {
      const roles = row.product.meta.supportRoles || [];
      return roles.indexOf('soothing') !== -1 || roles.indexOf('barrier') !== -1 || roles.indexOf('hydrating') !== -1;
    }).length;
    if (intensity >= 2 && soothingOrBarrier === 0) {
      score -= 28;
      notes.push('unsupported-actives');
    } else if (soothingOrBarrier >= 2) {
      score += 8;
      notes.push('barrier-support');
    }

    // Decision #17: dry/sensitive (or dryness concern) — barrier moisturizer preferred.
    const needsBarrierProfile = skin === 'dry' || skin === 'sensitive' || concern === 'dryness';
    const moisturizer = rows.find((row) => row.step === 'moisturizer');
    if (needsBarrierProfile) {
      if (moisturizer) {
        const roles = moisturizer.product.meta.supportRoles || [];
        const barrierMoist =
          roles.indexOf('barrier') !== -1 ||
          roles.indexOf('soothing') !== -1 ||
          roles.indexOf('hydrating') !== -1;
        if (barrierMoist) {
          score += 16;
          notes.push('barrier-moisturizer');
        } else {
          score -= 14;
          notes.push('weak-barrier-moisturizer');
        }
        if (moisturizer.product.meta.strength === 'active') {
          score -= 20;
          notes.push('active-moisturizer-on-barrier-skin');
        }
      }
      if (intensity >= 2 && soothingOrBarrier === 0) {
        score -= 18;
        notes.push('barrier-required');
      }
    }

    // Texture progression: dry skin wants the routine to get richer, oily skin
    // wants it to stay light.
    if (moisturizer) {
      const texture = moisturizer.product.meta.texture;
      if ((skin === 'dry' || concern === 'dryness') && texture === 'cream') {
        score += 10;
        notes.push('texture:sealed');
      }
      if (skin === 'oily' && texture === 'cream') {
        score -= 8;
        notes.push('texture:heavy-finish');
      }
    }

    // Some brand variety reads as a curated routine rather than a shelf dump,
    // but a routine of five unrelated brands loses coherence.
    const brands = {};
    rows.forEach((row) => {
      const b = brandFamily(row.product);
      if (b) brands[b] = true;
    });
    const brandCount = Object.keys(brands).length;
    if (brandCount >= 2 && brandCount <= 4) {
      score += 6;
      notes.push('brands:' + brandCount);
    }

    return { score: Math.round(score * 10) / 10, notes: notes, onConcern: onConcern, intensity: intensity };
  }

  /**
   * Phase C: return a routine from the prebuilt MMR list.
   * First call builds the list once; `{ advance: true }` cycles («روتين بديل»).
   * After the list ends it loops from R1 (no empty state).
   */
  function buildRoutine(skin, concern, opts) {
    opts = opts || {};
    const persist = opts.persist !== false;
    const list = ensureRoutineList(skin, concern);
    if (!list.length) {
      // Last-resort legacy candidate (should be rare).
      const fallback = buildRoutineCandidate(skin, concern, getRecentIds(skin, concern), []);
      if (persist && fallback.length) rememberRoutine(skin, concern, fallback);
      return fallback;
    }

    if (opts.advance) {
      state.routineIndex = (state.routineIndex + 1) % list.length;
    }

    const chosen = list[state.routineIndex % list.length];
    const rows = (chosen.rows || []).map((row) => Object.assign({}, row));
    const serum = rows.find((r) => r.step === 'serum');
    if (serum) {
      serum.whyAnchor = chosen.whyAnchor || whyAnchor(serum.product, concern);
    }
    if (persist) rememberRoutine(skin, concern, rows);
    return rows;
  }

  /**
   * Which steps get HARD-REJECTED for zero concern relevance, distinct from
   * stepWantsConcernPriority (which only governs ranking/tier preference —
   * a softer "prefer if available" signal that stays safe even with thin
   * inventory). Only toner and moisturizer are hard-required:
   * - Toner is where the actual defect was (a wrinkles/dehydration toner in
   *   a pores routine) and has deep enough catalog coverage to require it.
   * - Moisturizer too, with the barrier exemption below.
   * Sunscreen and cleanser are PREFERRED-but-not-required. Sunscreen: the
   * catalog genuinely lacks explicit concern-tagged sunscreens for several
   * concerns (its core job is daily protection, not treatment). Cleanser:
   * skin-safety exclusions (e.g. no deep/BHA cleanser on dry skin) already
   * remove most acne/pores-treating cleansers for dry/sensitive skin, so a
   * hard concern requirement there conflicts with that existing safety rule
   * and can zero out an entire pair (2026-08-07 testing: dry×acne,
   * sensitive×acne). Hard-rejecting both dropped total routines 116→64,
   * then several pairs to 0, before this was scoped down to toner+moisturizer.
   */
  function stepRequiresConcernOrReject(step, concern) {
    return step === 'toner' || step === 'moisturizer';
  }

  /**
   * 2026-08-07 audit fix: a support step with literally zero concern
   * relevance must not silently reach the results UI — this is exactly how a
   * "Fine Lines" toner ended up in a "pores" routine. The one carve-out is
   * the existing dry/sensitive/dryness barrier priority (plan decision #17):
   * a genuinely soothing/barrier/hydrating moisturizer may still lead there
   * even without a concern-specific tag.
   */
  function stepMissesConcernHard(row, skin, concern) {
    if (!row || !row.product || row.step === 'serum') return false;
    if (!stepRequiresConcernOrReject(row.step, concern)) return false;
    const availability = state.concernAvailability || {};
    if (availability[row.step] === false) return false;
    // Same multi-signal "does this product support the concern" definition
    // already used for the serum hard check (intel concern_effects, then
    // active-family, then support-role fallback) — not the stricter
    // concernEvidence().fit, which only reads intel tags and zeroed out
    // pairs like oily×wrinkles where a light gel moisturizer's anti-aging
    // actives (adenosine/peptide) weren't separately tagged in intel.
    if (serumSupportsConcern(row.product, concern)) return false;
    if (
      (row.step === 'moisturizer' || row.step === 'toner') &&
      (skin === 'dry' || skin === 'sensitive' || concern === 'dryness')
    ) {
      // Plan decision #17: a gentle toner/barrier moisturizer chosen for
      // dry/sensitive comfort is correct even without concern-specific
      // tagging — treating everyone as an "acne toner" candidate would push
      // actives dry/sensitive skin shouldn't get in the first place.
      if (hasBarrierRole(row.product)) return false;
    }
    return true;
  }

  function getRoutineQualityIssues(rows, skin, concern) {
    const validRows = (rows || []).filter((row) => row && row.product && row.product.variantId);
    const issues = [];
    const steps = new Set(validRows.map((row) => row.step));
    STEPS.forEach((step) => {
      if (!steps.has(step)) issues.push('missing-' + step);
    });

    const seenActiveFamilies = {};
    let intensity = 0;
    validRows.forEach((row) => {
      if (!isHardEligible(row.product, row.step, skin, concern)) issues.push('ineligible-' + row.step);
      if (stepMissesConcernHard(row, skin, concern)) issues.push(row.step + '-misses-concern');
      treatmentActiveFamilies(row.product, row.step).forEach((family) => {
        if (seenActiveFamilies[family]) issues.push('duplicate-active-' + family);
        seenActiveFamilies[family] = true;
      });
      intensity += activeIntensity(row.product, row.step);
    });

    const maxIntensity = skin === 'sensitive' ? 2 : skin === 'dry' ? 3 : 4;
    if (intensity > maxIntensity) issues.push('active-overload');

    const serum = validRows.find((row) => row.step === 'serum');
    if (!serum || !serumSupportsConcern(serum.product, concern)) {
      issues.push('serum-misses-concern');
    }

    return [...new Set(issues)];
  }

  function getSelectedRoutineRows() {
    return state.routine.filter((row) => row.product && row.product.variantId);
  }

  /**
   * Display/cart readiness. Soft quality notes (duplicate-active, irritation budget)
   * must NOT blank the results UI — that was a Phase D Critical empty-state bug.
   * Hard blockers only: missing step, unavailable, ineligible slot, serum misses concern.
   */
  function isRoutineReady(rows) {
    const validRows = (rows || []).filter(
      (row) =>
        row &&
        row.product &&
        row.product.variantId &&
        row.product.available !== false
    );
    if (validRows.length !== STEPS.length) return false;
    const availableSteps = new Set(validRows.map((row) => row.step));
    if (!STEPS.every((step) => availableSteps.has(step))) return false;
    const issues = getRoutineQualityIssues(validRows, state.skin, state.concern);
    const hard = issues.filter(
      (issue) =>
        issue.indexOf('missing-') === 0 ||
        issue.indexOf('ineligible-') === 0 ||
        issue.indexOf('-misses-concern') !== -1
    );
    return hard.length === 0;
  }

  function isNoCodeDiscount() {
    return boot.noCodeDiscount === true || boot.discountViaCode === false;
  }

  function updateCheckoutUI() {
    const selected = getSelectedRoutineRows();
    const ready = isRoutineReady(selected);
    const compareRow = ui.querySelector('[data-arb-total-compare-row]');
    const compareEl = ui.querySelector('[data-arb-total-compare]');
    const saleEl = ui.querySelector('[data-arb-total-sale]');
    const saveEl = ui.querySelector('[data-arb-total-save]');
    const countEl = ui.querySelector('[data-arb-total-count]');
    const addLabel = ui.querySelector('[data-arb-add-label]');
    const addBtn = ui.querySelector('[data-arb-add]');
    const pct = Number(boot.discountPercent || 20);
    const noCode = isNoCodeDiscount();

    let saleTotal = 0;
    let compareTotal = 0;
    selected.forEach((row) => {
      const price = Number(row.product.price || 0);
      const compareAt = Number(row.product.compareAt || 0);
      saleTotal += price;
      compareTotal += compareAt > price ? compareAt : price;
    });

    // Code mode: catalog price is pre-discount; apply % for display.
    // No-code mode: show catalog sale total only (no before/after / savings).
    if (!noCode) {
      compareTotal = saleTotal;
      saleTotal = Math.round(saleTotal * (100 - pct) / 100);
    }

    if (noCode) {
      if (compareRow) compareRow.hidden = true;
      if (compareEl) compareEl.hidden = true;
      if (saveEl) {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    } else {
      if (compareRow) compareRow.hidden = false;
      if (compareEl) {
        const showCompare = compareTotal > saleTotal && saleTotal > 0;
        compareEl.textContent = formatMoney(compareTotal);
        compareEl.hidden = !showCompare;
        if (compareRow) compareRow.hidden = !showCompare;
      }
      if (saveEl) {
        const saved = compareTotal - saleTotal;
        saveEl.textContent = saved > 0 ? 'وفّرتِ ' + formatMoney(saved) : '';
        saveEl.hidden = saved <= 0;
      }
    }

    if (saleEl) {
      saleEl.textContent = saleTotal ? formatMoney(saleTotal) : formatMoney(0);
    }
    if (countEl) {
      countEl.textContent = selected.length ? selected.length + ' منتجات مختارة لكِ' : '';
      countEl.hidden = !selected.length;
    }
    if (addLabel) {
      addLabel.textContent = ready ? 'أضيفي الروتين للسلة' : 'الروتين غير جاهز';
    }
    if (addBtn) addBtn.disabled = !ready;
  }

  function getSkinGoalLabel(concern) {
    const goals = boot.goals || {};
    return goals[concern] || 'روتين متوازن + حماية البشرة';
  }

  function renderAnalysisSummary() {
    const card = ui.querySelector('[data-arb-analysis]');
    const skinEl = ui.querySelector('[data-arb-analysis-skin]');
    const concernEl = ui.querySelector('[data-arb-analysis-concern]');
    const goalEl = ui.querySelector('[data-arb-analysis-goal]');
    if (!card) return;

    const skinLabel = boot.labels[state.skin] || '';
    const concernLabel = boot.labels[state.concern] || '';
    if (!skinLabel || !concernLabel) {
      card.hidden = true;
      return;
    }

    if (skinEl) skinEl.textContent = skinLabel;
    if (concernEl) concernEl.textContent = concernLabel;
    if (goalEl) goalEl.textContent = getSkinGoalLabel(state.concern);
    card.hidden = false;
  }

  function buildExpertWhatsAppMessage(routine) {
    const rows = (routine || []).filter((row) => row && row.product);
    const skinLabel = boot.labels[state.skin] || '';
    const concernLabel = boot.labels[state.concern] || '';
    const productLines = rows
      .map((row, index) => (index + 1) + '. ' + (row.product.title || row.label || ''))
      .join('\n');

    return (
      'مرحباً سندس 🌸\n\n' +
      'سويت اختبار الروتين الذكي بالموقع، وطلع لي هذا الروتين لـ البشرة ' +
      skinLabel +
      ' ومشكلة ' +
      concernLabel +
      ':\n\n\n' +
      (productLines || '—') +
      '\n' +
      'حابة أتأكد منك قبل أطلب.. شو رأيك، مناسب لبشرتي؟ وهل فيه أي نصيحة إضافية؟ 💕'
    );
  }

  function updateExpertWhatsAppLink(routine) {
    const waLink = ui.querySelector('[data-arb-expert-wa]');
    if (!waLink) return;
    const phone = String(boot.expertWhatsapp || boot.feedbackWhatsapp || '970597298636').replace(/\D/g, '');
    const msg = buildExpertWhatsAppMessage(routine);
    waLink.href = 'https://wa.me/' + phone + '?text=' + encodeURIComponent(msg);
  }

  /**
   * Display-only pregnancy/breastfeeding caution.
   * Auto: retinol + retinal. Plus manual ban list (handles).
   * Never used by scoring or selection.
   */
  const PREGNANCY_UNSAFE_HANDLES = {
    // COSRX BHA Blackhead Power Liquid سائل BHA يخفي المسام والرؤوس السوداء 100ml
    'cosrx-bha-blackhead-power-liquid-سائل-bha-يخفي-المسام-والرؤوس-السوداء-100ml': true
  };
  const PREGNANCY_UNSAFE_RE = /retinaldehyde|retinol|retinal|ريتينول|ريتينال|ريتنول|ريتنال/;
  const PREGNANCY_RETINOID_ALT_RE =
    /بديل[-\s]*(?:ال)?(?:ريتينول|ريتنول|ريتينال|ريتنال)|alternative\s+to\s+retinol|retinol\s+alternative|bakuchiol|باكوتشيول|باكوشيول/;

  function isPregnancyUnsafe(product) {
    if (!product) return false;

    if (product.handle && PREGNANCY_UNSAFE_HANDLES[product.handle]) return true;

    const tags = product.tags || [];
    const nameText = normalize([product.title, product.handle, tags.join(' ')].join(' '));
    const body = product.bodyText || (product.bodyHtml ? plainText(product.bodyHtml) : '');
    const fullText = body ? nameText + ' ' + normalize(body) : nameText;

    // Skip bakuchiol / "بديل الريتينول" (name mentions retinol only as alternative)
    if (PREGNANCY_RETINOID_ALT_RE.test(nameText)) {
      const withoutAlt = nameText.replace(/بديل[-\s]*(?:ال)?(?:ريتينول|ريتنول|ريتينال|ريتنال)/g, ' ');
      if (!PREGNANCY_UNSAFE_RE.test(withoutAlt)) return false;
    }

    if (PREGNANCY_UNSAFE_RE.test(nameText)) return true;

    // Body: strong formula claims only (not marketing comparisons)
    if (
      /(?:contains?|with|يحتوي|بال)\s*.{0,20}(?:retinol|retinal|ريتينول|ريتينال|ريتنول|ريتنال)|(?:retinol|retinal|ريتينول|ريتينال|ريتنول|ريتنال)\s*\d|\d\s*%\s*(?:retinol|retinal)/.test(
        fullText
      )
    ) {
      return !PREGNANCY_RETINOID_ALT_RE.test(nameText);
    }

    return false;
  }


  function renderProductCard(row) {
    const p = row.product;
    const img = p.image || '';
    const pregnancyWarn = isPregnancyUnsafe(p);
    const swapping = state.swappingStep === row.step;
    const tagLine = productTagLine(p, row.step, state.skin, state.concern);
    const tagHtml = tagLine ? '<p class="arb__item-tags">' + escapeHtml(tagLine) + '</p>' : '';
    const pregnancyBadge = pregnancyWarn
      ? '<span class="arb__pregnancy-badge" role="note">⚠️ غير مناسب للحامل والمرضع</span>'
      : '';
    const swapBtn = pregnancyWarn
      ? '<button type="button" class="arb__item-swap" data-arb-swap="' +
        row.step +
        '" aria-label="بدّلي ' +
        escapeHtml(row.label) +
        '" title="بدّلي المنتج">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M16 3l4 4-4 4M8 21l-4-4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M20 7H9a4 4 0 0 0-4 4v2M4 17h11a4 4 0 0 0 4-4v-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '<span>بدّلي</span>' +
        '</button>'
      : '';
    return (
      '<article class="arb__item' +
        (pregnancyWarn ? ' arb__item--pregnancy-warn' : '') +
        (swapping ? ' is-swapping' : '') +
        '" data-arb-item="' +
        row.step +
        '">' +
        '<span class="arb__item-num" aria-hidden="true">' +
        row.index +
        '</span>' +
        swapBtn +
        (img
          ? '<a class="arb__item-img-link" href="' +
            p.url +
            '"><img class="arb__item-img" src="' +
            img +
            '" alt="" loading="lazy" width="64" height="64"></a>'
          : '<div class="arb__item-img"></div>') +
        '<div class="arb__item-body">' +
          '<span class="arb__item-step">' +
          row.label +
          '</span>' +
          '<p class="arb__item-title"><a href="' +
          p.url +
          '">' +
          escapeHtml(p.title) +
          '</a></p>' +
          tagHtml +
          pregnancyBadge +
        '</div>' +
        renderPriceBlock(p) +
        '<div class="arb__item-loader" aria-hidden="true"><div class="arb__spinner"></div></div>' +
      '</article>'
    );
  }

  /**
   * 2026-08-07 (user-reported, two bugs):
   * 1) The loop called buildRoutine WITHOUT `advance: true`, so every
   *    iteration re-read the exact same entry at the current routineIndex.
   *    isAcceptablePregnancyRoutineSwap always rejected it (same serum id as
   *    itself), the loop exhausted with accepted=null, and "بدّلي" silently
   *    did nothing.
   * 2) Even fixed, cycling the WHOLE routine to find a pregnancy-safe SERUM
   *    doesn't address a pregnancy-unsafe product on any OTHER step (e.g. a
   *    flagged moisturizer) — nothing checked that the step the shopper
   *    actually clicked "بدّلي" on became safe in the new routine, and all 4
   *    other products changed along with it for no reason. The swap button
   *    appears per-product (any step), so it must replace that ONE product,
   *    not gamble on a full routine cycle.
   *
   * Now: look across the already-built alternative routines (ensureRoutineList)
   * for a different, pregnancy-safe product at this SAME step, and splice only
   * that one row into the current routine. Every candidate came from a fully
   * vetted routine (skin/concern-eligible), so this stays safe without
   * duplicating the full scoring pipeline for a single-step swap.
   */
  async function swapProduct(step) {
    if (state.swappingStep || !state.skin || !state.concern) return;
    const rowIndex = state.routine.findIndex((row) => row.step === step);
    if (rowIndex === -1) return;
    const currentProduct = state.routine[rowIndex].product;
    const currentId = currentProduct ? currentProduct.id : null;

    state.swappingStep = step;
    renderResults(state.routine);

    try {
      await ensureCatalog(state.skin, state.concern);
      const list = ensureRoutineList(state.skin, state.concern);
      let replacement = null;
      for (const entry of list) {
        const row = (entry.rows || []).find((r) => r.step === step);
        if (!row || !row.product) continue;
        if (row.product.id === currentId) continue;
        if (isPregnancyUnsafe(row.product)) continue;
        replacement = row;
        break;
      }
      if (replacement) {
        const nextRoutine = state.routine.map((row, i) =>
          i === rowIndex ? Object.assign({}, replacement) : row
        );
        state.routine = nextRoutine;
        rememberRoutine(state.skin, state.concern, nextRoutine);
        state.discountApplied = false;
      }
    } catch (err) {
      console.warn('[AI Routine] swap failed', err);
    } finally {
      state.swappingStep = null;
      renderResults(state.routine);
    }
  }

  function bindResultsEvents() {
    const box = ui.querySelector('[data-arb-results]');
    if (!box || box._arbBound) return;
    box._arbBound = true;

    box.addEventListener('click', (event) => {
      const swapBtn = event.target.closest('[data-arb-swap]');
      if (!swapBtn) return;
      event.preventDefault();
      swapProduct(swapBtn.getAttribute('data-arb-swap'));
    });
  }

  function renderResults(routine) {
    const box = ui.querySelector('[data-arb-results]');
    const empty = ui.querySelector('[data-arb-empty]');
    const checkout = ui.querySelector('[data-arb-checkout]');
    const summary = ui.querySelector('[data-arb-summary]');
    const analysis = ui.querySelector('[data-arb-analysis]');
    const expert = ui.querySelector('[data-arb-expert]');

    summary.textContent =
      'لبشرة ' +
      (boot.labels[state.skin] || '') +
      ' · تركيز على ' +
      (boot.labels[state.concern] || '');

    renderAnalysisSummary();

    if (!isRoutineReady(routine)) {
      box.hidden = true;
      checkout.hidden = true;
      if (expert) expert.hidden = true;
      // Keep analysis visible so the shopper can navigate back with context.
      renderAnalysisSummary();
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    box.hidden = false;
    checkout.hidden = false;
    if (expert) expert.hidden = false;

    box.innerHTML = routine.map(renderProductCard).join('');
    updateCheckoutUI();
    updateExpertWhatsAppLink(routine);
    if (isNoCodeDiscount()) {
      state.discountApplied = true;
      setDiscountStatus('no-code');
    } else {
      // No network call here. renderResults runs on every shuffle/swap/step toggle,
      // and hitting a discount endpoint that often is what gets the shopper's session
      // flagged by Shopify bot protection (the "verify you are human" wall on /cart).
      // The code is applied once, on add-to-cart, in applyDiscountCode().
      setDiscountStatus('applied', boot.discountCode || 'Sondos');
    }
  }

  function setDiscountStatus(status, code) {
    const dealTitle = ui.querySelector('.arb__deal-title');
    const dealSub = ui.querySelector('.arb__deal-sub');
    const trust = ui.querySelector('[data-arb-checkout-trust]');

    if (status === 'no-code') {
      if (dealTitle) dealTitle.textContent = 'خصم التوجيهي مفعّل على الأسعار';
      if (dealSub) dealSub.textContent = 'بدون كود — السعر الظاهر هو بعد الخصم';
      if (trust) trust.textContent = 'يمكنك مراجعة السلة قبل تأكيد الطلب';
      return;
    }

    if (status === 'applied') {
      if (dealTitle) dealTitle.textContent = 'خصمك مفعّل تلقائياً';
      if (dealSub) dealSub.textContent = 'سيُطبَّق تلقائياً عند الدفع';
      if (trust) trust.textContent = 'يمكنك مراجعة السلة قبل تأكيد الطلب';
      return;
    }

    if (status === 'failed') {
      if (dealTitle) dealTitle.textContent = 'استخدمي كود الخصم عند الدفع';
      if (dealSub) dealSub.textContent = 'الكود: ' + code;
      if (trust) trust.textContent = 'راجعي الكود في صفحة الدفع قبل تأكيد الطلب';
      return;
    }

    if (dealTitle) dealTitle.textContent = 'جاري التحقق من الخصم';
    if (dealSub) dealSub.textContent = 'لحظات ونتأكد من تفعيله تلقائياً';
    if (trust) trust.textContent = 'يمكنك مراجعة السلة قبل تأكيد الطلب';
  }

  async function applyDiscountCode(code) {
    if (isNoCodeDiscount()) {
      setDiscountStatus('no-code');
      return true;
    }
    if (!code) return false;
    const normalizedCode = String(code).trim();
    if (!normalizedCode) return false;
    if (state.discountApplied) {
      setDiscountStatus('applied', normalizedCode);
      return true;
    }

    try {
      // Apply through the cart Ajax API, never through /discount/<code>. The /discount/
      // route is a bot-protected navigation endpoint; calling it from fetch() reads as
      // discount-code probing and gets the session challenged on /cart and /checkout.
      const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
      const response = await fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ discount: normalizedCode })
      });

      if (!response.ok) {
        throw new Error('Discount request failed with status ' + response.status);
      }

      state.discountApplied = true;
      setDiscountStatus('applied', normalizedCode);
      return true;
    } catch (err) {
      state.discountApplied = false;
      setDiscountStatus('failed', normalizedCode);
      console.warn('[AI Routine] discount apply failed', err);
      return false;
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  let catalogPromise = null;

  async function ensureCatalog(skin, concern) {
    const wantedHandles = [];
    wantedHandles.push(normalizeHandle(boot.collectionHandle || 'عناية-بالبشرة'));
    if (concern) {
      resolveConcernCollectionHandles(concern).forEach((h) => wantedHandles.push(h));
    }
    const seenHandles = {};
    const uniqueHandles = [];
    wantedHandles.filter(Boolean).forEach((h) => {
      const key = handleKey(h);
      if (seenHandles[key]) return;
      seenHandles[key] = true;
      uniqueHandles.push(h);
    });
    const needFetch = uniqueHandles.filter((h) => !state.loadedHandles[handleKey(h)]);
    const needAllProducts = !state.allProductsLoaded;
    if (!needFetch.length && !needAllProducts && state.catalogReady) return;
    if (catalogPromise) return catalogPromise;

    catalogPromise = (async () => {
      await ensureIntel();
      const merged = new Map();
      const seed = (state.products && state.products.length)
        ? state.products
        : (boot.seedProducts || []);
      seed.forEach((p) => merged.set(p.id, p));

      function mergeApiProducts(list, collectionHandle) {
        (list || []).forEach((p) => {
          const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
          if (!variant) return;
          const prev = merged.get(p.id) || {};
          const prevCollections = Array.isArray(prev.collections) ? prev.collections : [];
          const collections = collectionHandle
            ? [...new Set(prevCollections.concat([collectionHandle]))]
            : prevCollections;
          merged.set(p.id, Object.assign({}, prev, {
            id: p.id,
            title: p.title,
            handle: p.handle,
            vendor: p.vendor,
            tags: Array.isArray(p.tags) ? p.tags : [],
            // Descriptions are the only source of ingredient data in this
            // catalog; the Liquid seed omits them to keep the page light.
            bodyText: plainText(p.body_html) || prev.bodyText || '',
            available: !!variant.available,
            price: Math.round(parseFloat(variant.price) * 100),
            compareAt: variant.compare_at_price
              ? Math.round(parseFloat(variant.compare_at_price) * 100)
              : Number(prev.compareAt || 0),
            url: '/products/' + encodeURIComponent(p.handle),
            image: (p.images && p.images[0] && (p.images[0].src || p.images[0])) || null,
            variantId: variant.id,
            aiPriority: Number(prev.aiPriority || 0),
            collections: collections
          }));
        });
      }

      try {
        if (needAllProducts) {
          let completedAllProducts = false;
          for (let page = 1; page <= 3; page++) {
            const res = await fetch('/products.json?limit=250&page=' + page, {
              headers: { Accept: 'application/json' }
            });
            if (!res.ok) break;
            const data = await res.json();
            const list = data.products || [];
            mergeApiProducts(list, null);
            if (list.length < 250) {
              completedAllProducts = true;
              break;
            }
            if (page === 3) completedAllProducts = true;
          }
          state.allProductsLoaded = completedAllProducts;
        }

        for (let hi = 0; hi < needFetch.length; hi++) {
          const handle = needFetch[hi];
          if (!handle) continue;
          let fetchedThisHandle = false;
          for (let page = 1; page <= 5; page++) {
            const res = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=50&page=' + page, {
              headers: { Accept: 'application/json' }
            });
            if (!res.ok) break;
            fetchedThisHandle = true;
            const data = await res.json();
            const list = data.products || [];
            if (!list.length) break;
            mergeApiProducts(list, handle);
            if (list.length < 50) break;
          }
          if (fetchedThisHandle) {
            state.loadedHandles[handleKey(handle)] = true;
          }
        }
      } catch (err) {
        console.warn('[AI Routine] catalog fetch failed', err);
      }

      state.products = [...merged.values()].map(enrich);
      state.catalogReady = true;
    })();

    try {
      await catalogPromise;
    } finally {
      catalogPromise = null;
    }
  }

  async function runRecommendation() {
    showPanel('results');
    const loading = ui.querySelector('[data-arb-loading]');
    const results = ui.querySelector('[data-arb-results]');
    const empty = ui.querySelector('[data-arb-empty]');
    const checkout = ui.querySelector('[data-arb-checkout]');
    const analysis = ui.querySelector('[data-arb-analysis]');
    const expert = ui.querySelector('[data-arb-expert]');
    loading.hidden = false;
    results.hidden = true;
    empty.hidden = true;
    checkout.hidden = true;
    if (expert) expert.hidden = true;
    // Show diagnosis immediately while catalog loads — reduces wait anxiety
    renderAnalysisSummary();
    const addBtn = ui.querySelector('[data-arb-add]');
    const success = ui.querySelector('[data-arb-success]');
    const trust = ui.querySelector('[data-arb-checkout-trust]');
    if (addBtn) addBtn.hidden = false;
    if (success) success.hidden = true;
    if (trust) trust.hidden = false;

    try {
      await ensureCatalog();
      // second pass after answers: bring concern-focused collections for higher confidence
      await ensureCatalog(state.skin, state.concern);
      // Rebuild list for this skin×concern (once); shuffle only advances index later.
      state.routineList = [];
      state.routineListKey = null;
      state.routineIndex = 0;
      state.routine = buildRoutine(state.skin, state.concern);
      // If the top pick fails hard readiness, advance within the prebuilt list.
      if (!isRoutineReady(state.routine) && state.routineList.length > 1) {
        for (let i = 0; i < state.routineList.length; i++) {
          state.routine = buildRoutine(state.skin, state.concern, { advance: true, persist: false });
          if (isRoutineReady(state.routine)) break;
        }
      }
      renderResults(state.routine);
      notifyJourneyArbCompleted(state.routine);
    } catch (err) {
      console.error('[AI Routine]', err);
      results.hidden = true;
      checkout.hidden = true;
      if (expert) expert.hidden = true;
      renderAnalysisSummary();
      empty.hidden = false;
    } finally {
      loading.hidden = true;
    }
  }

  /**
   * Behavior insights (cart attributes / Hotjar). Display-only side channel —
   * does not feed scoring. Fires once per successful runRecommendation.
   */
  function notifyJourneyArbCompleted(routine) {
    if (!isRoutineReady(routine)) return;
    try {
      const products = (routine || [])
        .filter((row) => row && row.product)
        .map((row) => {
          const step = boot.labels[row.step] || row.step || '';
          const name = row.product.handle || row.product.title || '';
          return String(step && name ? step + ':' + name : name || step).slice(0, 48);
        })
        .filter(Boolean);
      document.dispatchEvent(
        new CustomEvent('sondos:arb:completed', {
          detail: {
            skin: state.skin,
            concern: state.concern,
            skinLabel: boot.labels[state.skin] || state.skin || '',
            concernLabel: boot.labels[state.concern] || state.concern || '',
            products: products
          }
        })
      );
    } catch (err) {
      /* insights optional */
    }
  }

  async function fetchCartSections(sectionIds) {
    const ids = (sectionIds || []).filter(Boolean);
    if (!ids.length) return {};
    const base = window.location.pathname || '/';
    const url = base + (base.indexOf('?') >= 0 ? '&' : '?') + 'sections=' + encodeURIComponent(ids.join(','));
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return {};
    return res.json();
  }

  function sectionInner(html, selector) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = (selector && doc.querySelector(selector)) || doc.querySelector('.shopify-section') || doc.body;
    return node ? node.innerHTML : '';
  }

  function updateCartIconBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const html = sectionInner(sections['cart-icon-bubble']);
    if (html) bubble.innerHTML = html;
  }

  const NOTIFICATION_ITEM_SELECTOR = '.cart-notification-product__item';

  /**
   * Picks the cards for the items this add actually created, in the order the routine
   * lists them. A routine adds several products at once, so showing only the first
   * would under-report what went into the cart.
   */
  function notificationItemsHTML(sectionHTML, itemKeys) {
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const picked = (itemKeys || [])
      .map((key) => key && doc.getElementById('cart-notification-product-' + key))
      .filter(Boolean);

    if (picked.length) return picked.map((node) => node.outerHTML).join('');

    const all = doc.querySelectorAll(NOTIFICATION_ITEM_SELECTOR);
    return all.length
      ? Array.from(all).map((node) => node.outerHTML).join('')
      : sectionInner(sectionHTML);
  }

  /** Store uses cart_type = notification — open popup with View cart / Checkout */
  function openCartNotification(sections, itemKeys, count) {
    const notify = document.querySelector('cart-notification');
    if (!notify) return false;

    try {
      const prodHost = document.getElementById('cart-notification-product');
      if (prodHost && sections['cart-notification-product']) {
        prodHost.innerHTML = notificationItemsHTML(sections['cart-notification-product'], itemKeys);
      }

      // Lets the promo box price exactly the lines shown above.
      notify.dataset.addedItemKeys = (itemKeys || []).filter(Boolean).join(',');

      const btn = document.getElementById('cart-notification-button');
      if (btn && sections['cart-notification-button']) {
        const text = sectionInner(sections['cart-notification-button']).replace(/<[^>]+>/g, '').trim();
        btn.textContent = text || ('عرض السلة (' + count + ')');
      }

      updateCartIconBubble(sections);

      if (typeof notify.setActiveElement === 'function') {
        notify.setActiveElement(document.activeElement);
      }
      if (typeof notify.open === 'function') {
        notify.open();
        return true;
      }
    } catch (err) {
      console.warn('[AI Routine] notification open failed', err);
    }
    return false;
  }

  function openCartDrawer(sections) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !sections || !sections['cart-drawer']) return false;
    try {
      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents({ id: Date.now(), sections: sections });
        return true;
      }
    } catch (err) {
      console.warn('[AI Routine] drawer render failed', err);
    }
    try {
      if (typeof drawer.open === 'function') drawer.open();
      return true;
    } catch (e) {}
    return false;
  }

  function showInlineCartSuccess() {
    const addBtn = ui.querySelector('[data-arb-add]');
    const success = ui.querySelector('[data-arb-success]');
    const trust = ui.querySelector('[data-arb-checkout-trust]');
    if (addBtn) addBtn.hidden = true;
    if (trust) trust.hidden = true;
    if (success) success.hidden = false;
  }

  async function addRoutineToCart() {
    const btn = ui.querySelector('[data-arb-add]');
    const spinner = btn.querySelector('.arb__btn-spinner');
    const msg = ui.querySelector('[data-arb-cart-msg]');
    const selectedRows = getSelectedRoutineRows();
    if (!isRoutineReady(selectedRows)) {
      msg.textContent = 'الروتين غير مكتمل بعد. الغسول والمرطب وواقي الشمس خطوات أساسية.';
      msg.className = 'arb__cart-msg is-err';
      msg.hidden = false;
      return;
    }
    const allItems = selectedRows
      .filter((row) => row.product && row.product.variantId)
      .map((row) => ({
        id: Number(row.product.variantId),
        quantity: 1,
        // Underscore = hidden on storefront/checkout; visible in Admin order line items
        properties: { '_Added by': 'AI' }
      }))
      .filter((row) => row.id > 0);

    if (!allItems.length) {
      msg.textContent = 'اختاري منتجًا واحدًا على الأقل لإضافته للسلة.';
      msg.className = 'arb__cart-msg is-err';
      msg.hidden = false;
      return;
    }

    btn.disabled = true;
    spinner.hidden = false;
    msg.hidden = true;

    try {
      const notify = document.querySelector('cart-notification');
      const drawer = document.querySelector('cart-drawer');
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cartData = cartRes.ok ? await cartRes.json() : { items: [] };
      const existingVariantIds = new Set((cartData.items || []).map((it) => Number(it.variant_id || it.id)));
      const items = allItems.filter((it) => !existingVariantIds.has(it.id));

      if (!items.length) {
        msg.textContent = 'كل منتجات الروتين موجودة بالفعل في السلة.';
        msg.className = 'arb__cart-msg';
        msg.hidden = false;
        return;
      }

      const skippedCount = allItems.length - items.length;
      const sectionIds = notify
        ? ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble']
        : ['cart-drawer', 'cart-icon-bubble'];

      const res = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      });

      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error('تعذّرت الإضافة للسلة');
      }

      if (!res.ok || data.status || data.message) {
        throw new Error(data.description || data.message || 'تعذّرت الإضافة للسلة');
      }

      const added = data.items || (data.key ? [data] : []);
      const itemKeys = added.map((item) => item && item.key).filter(Boolean);
      if (!itemKeys.length && data.key) itemKeys.push(data.key);
      let sections = data.sections || {};

      if (!sections['cart-icon-bubble'] || (notify && !sections['cart-notification-button'])) {
        sections = Object.assign({}, sections, await fetchCartSections(sectionIds));
      }

      const opened = notify
        ? openCartNotification(sections, itemKeys, items.length)
        : openCartDrawer(sections);

      if (!opened) updateCartIconBubble(sections);

      if (!isNoCodeDiscount()) {
        await applyDiscountCode(boot.discountCode || 'SONDOS');
      }

      showInlineCartSuccess();
      try {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'arb', itemsAdded: items.length }
          })
        );
      } catch (e) {
        /* ignore */
      }
      if (skippedCount > 0) {
        msg.textContent = 'تمت إضافة ' + items.length + ' منتج، وتخطي ' + skippedCount + ' موجود مسبقًا.';
        msg.className = 'arb__cart-msg';
        msg.hidden = false;
      } else {
        msg.hidden = true;
      }
    } catch (err) {
      console.error('[AI Routine] cart', err);
      msg.textContent = err.message || 'حدث خطأ أثناء الإضافة للسلة';
      msg.className = 'arb__cart-msg is-err';
      msg.hidden = false;
    } finally {
      btn.disabled = false;
      spinner.hidden = true;
    }
  }

  // Events
  ui.querySelector('[data-arb-start]').addEventListener('click', startQuizFlow);

  ui.querySelectorAll('[data-step-dot]').forEach((dot) => {
    const activate = () => {
      if (dot.getAttribute('aria-disabled') === 'true') return;
      goToProgressStep(dot.getAttribute('data-step-dot'));
    };
    dot.addEventListener('click', activate);
    dot.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      activate();
    });
  });

  // Display only — the selected state was carried by a CSS class alone, so a
  // shopper on a screen reader got no feedback about what she had picked.
  // Nothing here feeds selection or scoring.
  function markChoice(el, on) {
    el.classList.toggle('is-selected', on);
    if (el.hasAttribute('role')) el.setAttribute('aria-selected', on ? 'true' : 'false');
  }

  ui.querySelectorAll('[data-skin]').forEach((btn) => {
    btn.addEventListener('click', () => {
      ui.querySelectorAll('[data-skin]').forEach((b) => markChoice(b, false));
      markChoice(btn, true);
      state.skin = btn.getAttribute('data-skin');
      setTimeout(() => showPanel('concern'), 160);
    });
  });

  ui.querySelectorAll('[data-concern]').forEach((btn) => {
    btn.addEventListener('click', () => {
      ui.querySelectorAll('[data-concern]').forEach((b) => markChoice(b, false));
      markChoice(btn, true);
      state.concern = btn.getAttribute('data-concern');
      runRecommendation();
    });
  });

  ui.querySelectorAll('[data-arb-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (state.panel === 'concern') showPanel('skin');
      else if (state.panel === 'skin') showPanel('landing');
      else if (state.panel === 'results') showPanel(state.concern ? 'concern' : 'skin');
    });
  });

  ui.querySelectorAll('[data-arb-back-concern]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!state.skin) {
        showPanel('skin');
        return;
      }
      showPanel('concern');
    });
  });

  ui.querySelectorAll('[data-arb-restart]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.skin = null;
      state.concern = null;
      state.routine = [];
      state.routineList = [];
      state.routineIndex = 0;
      state.routineListKey = null;
      state.selectedSteps = {};
      state.swappingStep = null;
      state.discountApplied = false;
      ui.querySelectorAll('.arb__choice.is-selected').forEach((b) => markChoice(b, false));
      showPanel('skin');
    });
  });

  const shuffleBtn = ui.querySelector('[data-arb-shuffle]');
  if (shuffleBtn) {
    shuffleBtn.addEventListener('click', () => {
      if (!state.skin || !state.concern) return;
      state.discountApplied = false;
      // Advance prebuilt list only — no full rebuild (Gate Perf-Routine).
      const t0 = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      state.routine = buildRoutine(state.skin, state.concern, { advance: true });
      state.lastShuffleMs =
        (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - t0;
      renderResults(state.routine);
    });
  }

  ui.querySelector('[data-arb-add]').addEventListener('click', addRoutineToCart);

  bindResultsEvents();

  // Seed immediately
  try {
    state.products = (boot.seedProducts || []).map(enrich);
  } catch (err) {
    console.error('[AI Routine] seed failed', err);
    state.products = boot.seedProducts || [];
  }
})();
